// Group-selection rules decide what happens to a proposal option's existing
// items when a new product is added for a product group (a catalog `uigroup`).
//
// A rule receives the current items, the item being added, and the sibling
// plan codes for that group, and returns the resolved items array.
//
// To change how a specific group behaves, add an entry to
// GROUP_SELECTION_RULES keyed by group id (e.g. 'coexist' for groups whose
// plans can be selected together instead of replacing one another).

import {getPriceForCountry, getCurrencyForCountry} from './getPriceForCountry'
import {
  KEAP_FAMILY_ANNUAL_CODES,
  KEAP_FAMILY_LEGACY_CODES,
  KEAP_FAMILY_LEGACY_ANNUAL_CODES,
  GROWTH_PACKAGES,
  FREE_ACCOUNT_CODES,
  MC_PLANS_CODE,
  MC_TRIAL_PLAN_CODES,
  MC_2_0_PLANS,
  MC_2_0_SIGPREMIERPLANS,
  THRYV_STARTER_CODES,
} from '../../../utils/constants'
import {
  THRYV_BASIC_CODES,
  isThryvBasicCode,
  isTgpFoundationFamilyCode,
  isTgpFoundationBlockingCode,
  isTgpFamilyCode,
  isTgpSignatureCode,
} from '../../../utils/tgp-catalog-flags'

// Returns whether the account can have Thryv Leads and Marketing Center in
// the cart together. Mirrors new-order.js:171-177/upgrade-order.js:276-284's
// `isNewTLMCAccessible = isTLMCAccess` — TL_MC_Access alone. TL_Tax_Plan_Enabled
// is a separate, unrelated flag that only picks which Thryv Leads SKU variant
// (tl-mc-5-thryv-m2m vs tl-tax-5-thryv-m2m) is offered, so it must not gate
// this too.
export function getIsNewTLMCAccessible(userFeatures = {}) {
  return Boolean(userFeatures.TL_MC_Access)
}

const WEBSITE_ADDON_CODE = 'addon-5-website_difm'
const WEBSITE_ENTITLEMENT_CODE = 'ent-5-website'
const WEBSITE_DESIGN_FEE_CODE = 'thryvdesg2'

// Legacy MC 5.0 plans that can't share an account with an MC 2.0 (Signature/
// TGP) plan. MC Foundation is deliberately excluded (see resolveItemsAfterAdd).
const LEGACY_MC5_CODES = [...MC_PLANS_CODE, 'mc_pro_enh-5-thryv-m2m']

// MC 2.0 plans that block the standalone Professionally Designed Website
// add-on (addon-5-website_difm) — either in the proposal option or on the
// existing account.
const WEBSITE_ADDON_BLOCKED_BY_CODES = MC_2_0_PLANS

export const SOCIAL_CONTENT_GEN_ADDON_CODE = 'addon-6-mc-socialcontentgen-m2m'
export const SOCIAL_CONTENT_GEN_ENTITLEMENT_CODE = 'ent-5-socialcontentgen'
export const BC_UNLIMITED_CODE = 'bc_unlm-5-thryv-m2m'
const BC_PROFESSIONAL_CODE = 'bc_pro-5-thryv-m2m'
// BC Professional accounts that already had the entitlement before this date
// keep it (grandfathered); new BC Professional purchases don't get it at all
// — only BC Unlimited does.
const SOCIAL_CONTENT_GEN_GRANDFATHER_CUTOFF = new Date('2024-02-08')

// Real Recurly subscription objects (allAccountSubs, sourced from
// subDetails.parentSubscriptions/childSubscriptions) nest the product code
// under `sub.plan.code`, not a top-level `sub.code`. Falls back to `sub.code`
// for the flat {code, state} shape used by local-test account-sub overrides.
export function getSubPlanCode(sub) {
  return sub?.plan?.code ?? sub?.code
}

// Mirrors options.js:3492-3499's isBCProfessionalIncluded/purchasedBeforeCutoff
// check — whether this account's existing BC Professional subscription
// predates the cutoff and so keeps its Social Content Generation entitlement.
export function isSocialContentGenGrandfathered(allAccountSubs = []) {
  const bcProSub = allAccountSubs.find(
    sub => getSubPlanCode(sub) === BC_PROFESSIONAL_CODE,
  )
  if (!bcProSub?.createdAt) return false
  return new Date(bcProSub.createdAt) < SOCIAL_CONTENT_GEN_GRANDFATHER_CUTOFF
}

// True once neither trigger for the Website Design Fee remains in the cart —
// the standalone add-on, or a Center's checked ent-5-website entitlement.
function hasWebsiteTrigger(items) {
  return items.some(
    item =>
      item.productCode === WEBSITE_ADDON_CODE ||
      item.addons?.some(
        addon => addon.productCode === WEBSITE_ENTITLEMENT_CODE,
      ),
  )
}

// A plan is available for a country when its currencies array lists the
// currency that country trades in (see getCurrencyForCountry), and that
// price entry's own `needFlag` (if any) is enabled
// Enable_GP_For_AU_And_NZ gating Leads Lift/Growth Package AUD & NZD prices.
// Plans with no currencies array are treated as universally available.
function isPlanAllowedForCountry(
  plan,
  country,
  userFeatures = {},
  ignoreNeedFlag = false,
) {
  if (!Array.isArray(plan.currencies) || plan.currencies.length === 0) {
    return true
  }
  const currency = getCurrencyForCountry(country)
  const priceEntry = plan.currencies.find(c => c.currency === currency)
  if (!priceEntry) return false
  if (
    !ignoreNeedFlag &&
    priceEntry.needFlag &&
    !userFeatures[priceEntry.needFlag]
  ) {
    return false
  }
  return true
}

// Restricted Access and HIPAA are mutually-exclusive substitutes gated by
// the client's industry (mirrors screens/catalog.js:795-799,1904-1915):
// HIPAA-compliant industries (dentists, health & wellness, healthcare
// providers) get the HIPAA add-on; everyone else gets the generic Restricted
// Access add-on instead — except in Barbados/Cayman, which show Restricted
// Access regardless of industry. The addon-5-hipaa <-> addon-5-restricted
// mutual exclusion itself (each excludes the other in the catalog's own
// `excludes` field) and HIPAA's requiresCenter (bc-6-thryv-m2m) are already
// handled generically by resolveItemsAfterAdd/getDisabledPlanCodes — no
// special-casing needed for those.
const HIPAA_COMPLIANT_INDUSTRIES = [
  '52k10f2ml8m1f41u', // dentists
  'pcqjcmfqjvknm31t', // health & wellness
  'e8cgl2m7jp221sx7', // healthcare providers
]
const HIPAA_ADDON_CODE = 'addon-5-hipaa'
const RESTRICTED_ACCESS_CODE = 'addon-5-restricted'
const RESTRICTED_ACCESS_COUNTRY_EXCEPTIONS = ['BB', 'KY']

// // Plans that are only available in specific countries — not available
// // per-plan from the catalog API (no country field on plan objects), so
// // this is a local allow-list rather than catalog-driven, same convention
// // as the other hardcoded fallback rules in this file. Add an entry here to
// // restrict another plan the same way (e.g. Leads Lift, AU-only).
// const COUNTRY_RESTRICTED_PLAN_CODES = {
//   'addon-5-leadslift-m2m': ['AU'],
// }

// function isPlanAllowedForCountry(planCode, country) {
//   const allowedCountries = COUNTRY_RESTRICTED_PLAN_CODES[planCode]
//   if (!allowedCountries) return true
//   return allowedCountries.includes(country)
// }

// Starter/Expanded Growth Package are mutually exclusive tiers — adding
// either removes the other (fresh session or account-owned/alreadyIncluded
// upgrade item alike). See resolveItemsAfterAdd and getDisabledPlanCodes.
const [GROWTH_PACKAGE_EXPANDED, GROWTH_PACKAGE_STARTER] = GROWTH_PACKAGES

const LISTING_REFRESH_CODES = [
  'addon-4-listrefrsh-m2m',
  'addon-5-listrefrsh-m2m',
]

// Thryv Success Service group: Listing Refresh and Strategic Content are
// mutually exclusive tiers, same pattern as the Growth Packages — adding
// either removes the other, and once Strategic Content is an account-owned
// (alreadyIncluded) upgrade item, downgrading back to Listing Refresh isn't
// allowed. See the Guard in resolveItemsAfterAdd and getDisabledPlanCodes.
export const LISTING_REFRESH_M2M_CODE = LISTING_REFRESH_CODES[1]
const STRATEGIC_CONTENT_CODE = 'addon-5-stratcontent-m2m'
export const THRYV_SUCCESS_SERVICE_CODES = [
  LISTING_REFRESH_M2M_CODE,
  STRATEGIC_CONTENT_CODE,
]

// Cascade-removal constants

export const MC_CENTER_CODES = [
  'mc_plus-5-thryv-m2m',
  'mc_pro-5-thryv-m2m',
  'mc_pro_enh-5-thryv-m2m',
  'mc_foundation-5-thryv-m2m',
  ...MC_TRIAL_PLAN_CODES,
]

// SEO + Thryv Leads + Leads Lift BNI products that depend on MC being in
// the cart — Leads Lift BNI cascade-removal is a deliberate fix over the
// legacy behavior (which left an already-added BNI item in place even
// after MC was removed).
const MC_SEO_TL_CASCADE_CODES = [
  'seobase-5-thryv-m2m',
  'addon-5-seokey-m2m',
  'addon-5-seopb-m2m',
  'tl-mc-5-thryv-m2m',
  'addon-5-leadslift-bni-m2m',
  LISTING_REFRESH_M2M_CODE,
  STRATEGIC_CONTENT_CODE,
]

const BC_6_CODE = 'bc-6-thryv-m2m'

// Leads Lift BNI can only be added once Marketing Center is already in the
// cart (mirrors the legacy centers-and-apps.js isMCInCart check — only
// these two MC codes count, not Foundation/Pro-Enhanced/trial variants).
const LEADS_LIFT_M2M_CODE = 'addon-5-leadslift-m2m'
const LEADS_LIFT_BNI_CODE = 'addon-5-leadslift-bni-m2m'
const MC_CODES_FOR_LEADS_LIFT_BNI = [
  'mc_plus-5-thryv-m2m',
  'mc_pro-5-thryv-m2m',
]

// Thryv Leads product family
const TL_CODES = ['tl-mc-5-thryv-m2m', 'tl-tax-5-thryv-m2m', 'tl-5-thryv-m2m']
const MC_FOUNDATION_CODE = 'mc_foundation-5-thryv-m2m'

// BC 5.0 tiers gated by Bc5newsales — new
// purchases blocked unless the account already owns one of these three.
// Deliberately narrower than OTHER_BC_CODES below (which also covers the
// 6.0-era bc_ent variants used by the unrelated BC-6-standalone cascade).
const BC_5_TIER_CODES = [
  'bc_plus-5-thryv-m2m',
  'bc_pro-5-thryv-m2m',
  'bc_unlm-5-thryv-m2m',
]

const OTHER_BC_CODES = [
  'bc_plus-5-thryv-m2m',
  'bc_pro-5-thryv-m2m',
  'bc_unlm-5-thryv-m2m',
  'bc_ent-6-thryv-m2m',
  'bc_ent-unlm-5-thryv-m2m',
]

// FULSAS-15052: owning any of these BC plans means every BC 5.0 tier is at or
// below it, so none of them may be added (a lower pick is a downgrade, the
// same-tier bc_unlm next to bc_ent-unlm would be a second BC on the order).
// bc_ent-unlm-5 shares bc_unlm-5's price/tier, which is why it blocks the
// whole 5.0 family rather than ranking inside BC_5_TIER_CODES.
const BC_OWNED_BLOCKS_ALL_5_TIER_CODES = [
  BC_6_CODE,
  'bc_ent-6-thryv-m2m',
  'bc_ent-unlm-5-thryv-m2m',
]

// FULSAS-15052: an account that already owns one of these paid Centers has been
// onboarded, so an upgrade must not add a fresh Onboarding Fee (thryvprfee).
// MC trial plans are excluded — their first paid Center is still a new
// onboarding.
export const PAID_CENTER_CODES = [
  ...MC_CENTER_CODES.filter(code => !MC_TRIAL_PLAN_CODES.includes(code)),
  ...MC_2_0_PLANS,
  ...OTHER_BC_CODES,
  BC_6_CODE,
]

const hasBusinessCenterInItems = items =>
  items.some(
    item =>
      BC_5_TIER_CODES.includes(item.productCode) ||
      OTHER_BC_CODES.includes(item.productCode) ||
      item.productCode === BC_6_CODE,
  )

const ALL_BC_PLAN_CODES = [
  ...new Set([...BC_5_TIER_CODES, ...OTHER_BC_CODES, BC_6_CODE]),
]

const hasKeapInItems = items =>
  items.some(item => KEAP_FAMILY_CODES.includes(item.productCode))

// MC 5.0 tiers, lowest to highest (same ordering idea as BC_5_TIER_CODES).
const MC_5_TIER_CODES = [
  'mc_plus-5-thryv-m2m',
  'mc_pro-5-thryv-m2m',
  'mc_pro_enh-5-thryv-m2m',
]

const HVAC_CODES = [
  'maxhvac-5-thryv-m2m',
  'maxroof-5-thryv-m2m',
  'maxelect-5-thryv-m2m',
  'maxplumb-5-thryv-m2m',
]

// All vertical clientsvcs variants (MC-bundled and standalone).
// Exported so OrderSummary can suppress the remove button for these items —
// they are auto-managed by the HVAC rules and must not be removed individually.
export const VERTICAL_CLIENTSVCS_CODES = [
  'vert_clientsvcs-5-thryv-ot',
  'vert_clientsvcs-5-thryv-m2m',
  'vert_mc_clientsvcs-5-thryv-m2m',
  'vert_mc_clientsvcs-5-thryv-ot',
  'vert_mc_web_clientsvcs-5-thryv-m2m',
  'vert_mc_web_clientsvcs-5-thryv-ot',
]

const VERTICAL_CLIENTSVCS_NAMES = {
  'vert_clientsvcs-5-thryv-ot': 'Vertical Client Services',
  'vert_clientsvcs-5-thryv-m2m': 'Vertical Client Services',
  'vert_mc_clientsvcs-5-thryv-ot': 'MC Vertical Client Services',
  'vert_mc_clientsvcs-5-thryv-m2m': 'MC Vertical Client Services',
  'vert_mc_web_clientsvcs-5-thryv-ot': 'MC Web Vertical Client Services',
  'vert_mc_web_clientsvcs-5-thryv-m2m': 'MC Web Vertical Client Services',
}

// Mirrors legacy options.js:2567-2731 decision tree for which vertical
// clientsvcs code accompanies an HVAC product.
//
// paymentSuffix is 'ot' (all-at-once) or 'm2m' (over-6-invoices) and comes
// from the HVAC panel's radio-button choice, preserved in the existing
// vertical code's suffix on subsequent cart mutations.
//
// The isMCPlusProInDefault guard (legacy options.js:2567) is derived from
// alreadyIncluded items — those are seeded from the account's active Recurly
// subscriptions by buildItemsFromRecurly, making a separate accountInfo prop
// unnecessary.
function resolveHvacVerticalCode(items, paymentSuffix) {
  // If MC Plus or MC Pro already exists on the account (not just in the
  // current proposal), legacy always falls back to the base variant regardless
  // of what MC tier is in the cart.
  const isMCPlusProInDefault = items.some(
    item =>
      (item.productCode === 'mc_plus-5-thryv-m2m' ||
        item.productCode === 'mc_pro-5-thryv-m2m') &&
      item.alreadyIncluded,
  )
  if (isMCPlusProInDefault) {
    return `vert_clientsvcs-5-thryv-${paymentSuffix}`
  }

  // MC Pro in cart — check whether it carries the ent-5-website entitlement
  // to decide between the web and non-web MC variant.
  // Mirrors legacy options.js:2621-2680: only MC Pro triggers the web check;
  // MC Plus and the base path do not.
  const mcProItem = items.find(
    item => item.productCode === 'mc_pro-5-thryv-m2m',
  )
  if (mcProItem) {
    const hasWebsite = (mcProItem.addons ?? []).some(
      a => (a.productCode ?? a.code) === 'ent-5-website',
    )
    return hasWebsite
      ? `vert_mc_web_clientsvcs-5-thryv-${paymentSuffix}`
      : `vert_mc_clientsvcs-5-thryv-${paymentSuffix}`
  }

  // MC Plus in cart (no MC Pro) — use non-web MC variant.
  const hasMcPlus = items.some(
    item => item.productCode === 'mc_plus-5-thryv-m2m',
  )
  if (hasMcPlus) return `vert_mc_clientsvcs-5-thryv-${paymentSuffix}`

  // No MC in cart — use base variant.
  return `vert_clientsvcs-5-thryv-${paymentSuffix}`
}

// Finds the active (non-account-owned) vertical clientsvcs item in the cart,
// extracts its payment suffix, and replaces it with the code that
// resolveHvacVerticalCode derives from the current cart state.
// Returns items unchanged if there is no non-account vertical to swap,
// or if the resolved code already matches what is there.
function swapHvacVertical(items, getConfig, selectedCountry) {
  const existing = items.find(
    item =>
      VERTICAL_CLIENTSVCS_CODES.includes(item.productCode) &&
      !item.alreadyIncluded,
  )
  if (!existing) return items

  // Preserve the payment type (ot = all-at-once, m2m = over-6-invoices)
  // chosen by the rep when the HVAC product was first added.
  const paymentSuffix = existing.productCode.includes('-ot') ? 'ot' : 'm2m'

  // Strip all non-account verticals before resolving so the resolution
  // function does not see the stale vertical while computing the new one.
  const withoutVerticals = items.filter(
    item =>
      !VERTICAL_CLIENTSVCS_CODES.includes(item.productCode) ||
      item.alreadyIncluded,
  )

  const correctCode = resolveHvacVerticalCode(withoutVerticals, paymentSuffix)
  if (correctCode === existing.productCode) return items

  const config = getConfig?.(correctCode) ?? null
  const isOt = correctCode.includes('-ot')
  return [
    ...withoutVerticals,
    toApiItem({
      productId: correctCode,
      name: VERTICAL_CLIENTSVCS_NAMES[correctCode] ?? correctCode,
      price: config
        ? getPriceForCountry(config.currencies, selectedCountry)
        : 0,
      productType: isOt ? 'ONE_TIME_FEE' : 'APP',
      billingFrequency: isOt ? null : 'monthly',
      quantity: 1,
      configuredOptions: {},
    }),
  ]
}

export const KEAP_FAMILY_CODES = [
  'keap_max-5-thryv-m2m',
  'keap_pro-5-thryv-m2m',
  'keap_ultimate-5-thryv-m2m',
  'keap_max-5-thryv-part-m2m',
  'keap_ultimate-5-thryv-part-m2m',
  'keap_max-5-thryv-annual',
  'keap_pro-5-thryv-annual',
  'keap_ultimate-5-thryv-annual',
  'keap_max-5-thryv-part-annual',
  'keap_ultimate-5-thryv-part-annual',
]

// M2M-only subset of KEAP_FAMILY_CODES (excludes annual variants)
const KEAP_FAMILY_M2M_CODES = KEAP_FAMILY_CODES.filter(
  code => !KEAP_FAMILY_ANNUAL_CODES.includes(code),
)

// Addons that should be restored from the account (not just removed) when removed during upgrade
const RESTORE_OR_REMOVE_CODES = [
  'addon-socialadsbronze-5-m2m',
  'addon-socialadssilver-5-m2m',
  'addon-socialadsgold-5-m2m',
  'addon-5-seopb-m2m',
  'addon-5-seokey-m2m',
  'addon-5-keap-addusers-m2m',
  'addon-5-keap-addcontacts-m2m',
  'addon-5-keap-textvoice-m2m',
  'addon-5-keap-addusers-annual',
  'addon-5-keap-addcontacts-annual',
  'addon-5-keap-textvoice-annual',
  'addon-5-ai-webbuilder',
]

// Maps legacy internal type strings to API schema productType values
const PRODUCT_TYPE_MAP = {
  software: 'CENTER',
  addon: 'APP',
  onetime: 'ONE_TIME_FEE',
  Center: 'CENTER',
  App: 'APP',
  OneTimeFees: 'ONE_TIME_FEE',
  // Pass-through for already-normalized values
  CENTER: 'CENTER',
  APP: 'APP',
  ONE_TIME_FEE: 'ONE_TIME_FEE',
}

function toApiAddon(addon) {
  return {
    productCode: addon.productCode ?? addon.code,
    productName: addon.productName ?? addon.name,
    productType:
      PRODUCT_TYPE_MAP[addon.productType ?? addon.addOnType] ??
      addon.productType ??
      'APP',
    ...(addon.quantity != null && {quantity: addon.quantity}),
    ...(addon.unitPrice != null
      ? {unitPrice: addon.unitPrice}
      : addon.unitAmount != null
      ? {unitPrice: addon.unitAmount}
      : addon.price != null
      ? {unitPrice: addon.price}
      : {}),
    ...(addon.dueTodayAmount != null && {dueTodayAmount: addon.dueTodayAmount}),
    ...(addon.billingFrequency != null && {
      billingFrequency: addon.billingFrequency,
    }),
    ...(addon.recurringAmount != null && {
      recurringAmount: addon.recurringAmount,
    }),
    ...(addon.configuration != null && {configuration: addon.configuration}),
    ...(addon.displayOrder != null && {displayOrder: addon.displayOrder}),
    ...(addon.alreadyIncluded != null && {
      alreadyIncluded: addon.alreadyIncluded,
    }),
  }
}

// Normalizes any item object to API schema fields + alreadyIncluded (UI-only flag).
// Handles both legacy field names (productId, name, price, type, configuredOptions)
// and already-normalized names (productCode, productName, unitPrice, productType, configuration).
// alreadyIncluded is retained in Redux state but stripped before the API call in useProposalSave.
function toApiItem(item) {
  return {
    productCode: item.productCode ?? item.productId,
    productName: item.productName ?? item.name,
    productType:
      PRODUCT_TYPE_MAP[item.productType ?? item.type] ??
      item.productType ??
      item.type,
    ...(item.quantity != null && {quantity: item.quantity}),
    ...(item.recurlyQuantity != null && {
      recurlyQuantity: item.recurlyQuantity,
    }),
    ...(item.unitPrice != null
      ? {unitPrice: item.unitPrice}
      : item.price != null
      ? {unitPrice: item.price}
      : {}),
    ...(item.dueTodayAmount != null && {dueTodayAmount: item.dueTodayAmount}),
    ...(item.billingFrequency != null && {
      billingFrequency: item.billingFrequency,
    }),
    ...(item.recurringAmount != null && {
      recurringAmount: item.recurringAmount,
    }),
    ...((item.configuration != null || item.configuredOptions != null) && {
      configuration: item.configuration ?? item.configuredOptions,
    }),
    ...(item.displayOrder != null && {displayOrder: item.displayOrder}),
    ...(item.addons?.length && {addons: item.addons.map(toApiAddon)}),
    ...(item.alreadyIncluded != null && {
      alreadyIncluded: item.alreadyIncluded,
    }),
    ...(item.productRank != null && {productRank: item.productRank}),
  }
}

const replaceWithinGroup = (items, newItem, groupPlanCodes) => [
  ...items.filter(item => !groupPlanCodes.includes(item.productCode)),
  newItem,
]

const coexistWithinGroup = (items, newItem) => [...items, newItem]

// add the custom rules as per the product groups
const customwithinGroup = (items, newItem) => [...items, newItem]

const RULES_BY_NAME = {
  replace: replaceWithinGroup,
  coexist: coexistWithinGroup,
  custom: customwithinGroup,
}

// groupId -> rule name. Groups not listed default to 'replace'.
const GROUP_SELECTION_RULES = {
  'Thryv Add-Ons': 'coexist',
  'Keap Add-Ons': 'coexist',
  'Social Boost': 'coexist',
  'Brand Boost': 'coexist',
}

// Per-product cart status helpers — one per product/group, each returning
// descriptively-named booleans so a caller (e.g. useProductsCatalog's
// handleAddProduct) can read intent directly off the destructure instead of
// writing a fresh items.some(...) pair. Add one of these per product group
// as new cross-group rules need it (mirrors getMarketingCenterStatus below).

export function getMarketingCenterStatus(items, newItem) {
  return {
    isNewMcAdd: MC_CENTER_CODES.includes(newItem.productCode),
    hasMarketingCenter: items.some(item =>
      MC_CENTER_CODES.includes(item.productCode),
    ),
    hasAlreadyIncludedMarketingCenter: items.some(
      item =>
        MC_CENTER_CODES.includes(item.productCode) && item.alreadyIncluded,
    ),
  }
}

export function getThryvSuccessServiceStatus(items) {
  return {
    hasThryvSuccessService: items.some(item =>
      THRYV_SUCCESS_SERVICE_CODES.includes(item.productCode),
    ),
  }
}

// Returns a Set of plan codes disabled by the currently selected items.
// Looks up disableApps/disableCenters from the catalog via getProductConfig
// rather than reading them from stored Redux items.
// allPlans: optional flat array of all catalog plans. When provided, plans
// whose requires/requiresCenter/requiresAddon are not yet in the cart are
// also added to the disabled set.
export function getDisabledPlanCodes(
  items,
  getProductConfig,
  allPlans = [],
  accountContext = {},
) {
  const codes = new Set()

  // Note: disableApps/disableCenters is NOT a preemptive disable in the
  // legacy catalog — centers-and-apps.js computes the equivalent aggregate
  // (lines 1006-1018) but only ever console.logs it, never applies it to
  // grey anything out. The real, functioning behavior lives in
  // options.js:2095-2098: adding a plan actively REMOVES any already-selected
  // items it disables. Mirrored as a removal rule in resolveItemsAfterAdd
  // instead — see the disableApps/disableCenters rule there.

  // Rule: excludes, coexist groups only (mirrors legacy checkIfExcluded).
  // 'replace' groups already swap siblings via Rule 1, so skip those.
  // Asymmetric excludes pairs fall back to Rule 2 in resolveItemsAfterAdd.
  items.forEach(item => {
    const config = getProductConfig?.(item.productCode) ?? null
    if (!config || GROUP_SELECTION_RULES[config.uigroup] !== 'coexist') return
    ;(config?.excludes ?? []).forEach(code => codes.add(code))
  })

  // Rule: protect alreadyIncluded items from being excluded.
  // If a catalog plan's excludes list contains an account-owned
  // (alreadyIncluded) product from a DIFFERENT group, that plan must be
  // grayed out — adding it would silently remove an existing subscription
  // with no group-swap safety net. Same-group siblings (e.g. Business
  // Center tiers, which each list the others in `excludes`) are exempt —
  // Rule 1's replaceWithinGroup already handles that swap safely, as an
  // explicit, visible tier-switch action (mirrors the coexist-only scoping
  // on the sibling excludes rule above).
  if (allPlans.length > 0) {
    const alreadyIncludedItems = items.filter(item => item.alreadyIncluded)
    allPlans.forEach(plan => {
      const excludesOwnedItemFromOtherGroup = alreadyIncludedItems.some(
        item => {
          if (!(plan.excludes ?? []).includes(item.productCode)) return false
          // TGP-DOWNGRADE #2 (to revert: delete this if-block).
          // An owned TGP plan must not grey out any other TGP plan (incl.
          // Foundation / Thryv Basic) — resolveItemsAfterAdd (Rule 1b) swaps
          // them in place and downgrades are permission-gated in
          // useCatalogCart.
          if (isTgpFamilyCode(plan.code) && isTgpFamilyCode(item.productCode))
            return false
          const ownedConfig = getProductConfig?.(item.productCode)
          return ownedConfig?.uigroup !== plan.uigroup
        },
      )
      if (excludesOwnedItemFromOtherGroup) {
        codes.add(plan.code)
      }
    })
  }

  // Rule: Expanded Growth Package blocks Starter Growth Package once it's
  // an account-owned (alreadyIncluded) upgrade item — no downgrading back
  // to Starter. A freshly-added (non-alreadyIncluded) Expanded doesn't
  // block Starter here; resolveItemsAfterAdd's mutual-exclusion swap
  // handles that case instead.
  const expandedAlreadyIncluded = items.some(
    item =>
      item.productCode === GROWTH_PACKAGE_EXPANDED && item.alreadyIncluded,
  )
  if (expandedAlreadyIncluded) {
    codes.add(GROWTH_PACKAGE_STARTER)
  }

  // Rule: BC 5.0 tier downgrade — once a higher BC tier is account-owned
  // (alreadyIncluded), lower tiers are greyed out. Mirrors the Growth Package
  // rule above. BC_5_TIER_CODES is ordered [plus, pro, unlm] so a higher index
  // means a higher tier.
  const ownedBcTierIndex = BC_5_TIER_CODES.reduce(
    (maxIdx, code, idx) =>
      items.some(item => item.productCode === code && item.alreadyIncluded)
        ? idx
        : maxIdx,
    -1,
  )
  if (ownedBcTierIndex > 0) {
    BC_5_TIER_CODES.slice(0, ownedBcTierIndex).forEach(code => codes.add(code))
  }

  // Rule: BC 6.0 / Enterprise downgrade protection (FULSAS-15052) — once
  // bc-6, bc_ent-6 or bc_ent-unlm-5 is account-owned (alreadyIncluded), all BC
  // 5.0 tiers are greyed out. BC-6 sits above the BC 5.0 family and
  // bc_ent-unlm-5 is the top (Unlimited) tier, so any 5.x pick is a downgrade
  // or a duplicate BC.
  const hasBcOwnedBlockingAll5 = items.some(
    item =>
      BC_OWNED_BLOCKS_ALL_5_TIER_CODES.includes(item.productCode) &&
      item.alreadyIncluded,
  )
  if (hasBcOwnedBlockingAll5) {
    BC_5_TIER_CODES.forEach(code => codes.add(code))
  }

  // Rule: MC 5.0 tier downgrade (FULSAS-15052) — once a higher MC tier is
  // account-owned (alreadyIncluded), lower tiers are greyed out so e.g. MC Pro
  // can't be switched to MC Plus. Upgrades stay available. Users holding
  // MC_Downgrade_Access (the existing Admin-only downgrade permission, see
  // useCatalogCart) keep the ability to downgrade.
  if (!accountContext.isMcDowngradeEnabled) {
    const ownedMcTierIndex = MC_5_TIER_CODES.reduce(
      (maxIdx, code, idx) =>
        items.some(item => item.productCode === code && item.alreadyIncluded)
          ? idx
          : maxIdx,
      -1,
    )
    if (ownedMcTierIndex > 0) {
      MC_5_TIER_CODES.slice(0, ownedMcTierIndex).forEach(code =>
        codes.add(code),
      )
    }
  }

  // Rule: FULSAS-14189 — TL disables both Growth Packages (US mutual exclusivity).
  // TL wins: if any TL variant is in the cart, GP Starter and GP Expanded are
  // greyed out so they can't be added. The removal on the add side is handled
  // by resolveItemsAfterAdd Rule 2c.
  const hasTLInCart = items.some(item => TL_CODES.includes(item.productCode))
  if (hasTLInCart) {
    GROWTH_PACKAGES.forEach(code => codes.add(code))
  }

  // // Rule: Strategic Content (Thryv Success Service) blocks Listing Refresh
  // // once it's an account-owned (alreadyIncluded) upgrade item — no
  // // downgrading back to Listing Refresh.
  // const strategicContentAlreadyIncluded = items.some(
  //   item => item.productCode === STRATEGIC_CONTENT_CODE && item.alreadyIncluded,
  // )
  // if (strategicContentAlreadyIncluded) {
  //   codes.add(LISTING_REFRESH_M2M_CODE)
  // }

  // Rule: GEP-7133 / FULSAS-13599 - legacy MC 5.0 plans are disabled while a
  // TGP (MC 2.0) plan is selected — they're a mutually exclusive in-place-
  // upgrade pairing (see the matching displacement rules in
  // resolveItemsAfterAdd/resolveItemsAfterRemove, both searchable by
  // "GEP-7133"). Adding TGP already removes MC 5.0 from the cart via those
  // rules; this additionally greys out the MC 5.0 card so it can't be
  // re-added while TGP is selected. TO REVERT: delete this block.
  const hasTgpPlanInCart = items.some(item =>
    MC_2_0_PLANS.includes(item.productCode),
  )
  if (hasTgpPlanInCart) {
    // MC Foundation stays selectable for TGP accounts — it's the downgrade
    // target; its catalog visibility is gated by TGP_Foundation_Catalog_Enabled.
    ;[...MC_PLANS_CODE, 'mc_pro_enh-5-thryv-m2m'].forEach(code =>
      codes.add(code),
    )
  }

  // Rule: Leads Lift BNI requires Marketing Center already in the cart.
  const hasMcForLeadsLiftBni = items.some(item =>
    MC_CODES_FOR_LEADS_LIFT_BNI.includes(item.productCode),
  )
  if (!hasMcForLeadsLiftBni) {
    codes.add(LEADS_LIFT_BNI_CODE)
  }

  // Rule: Professionally Designed Website — the standalone add-on
  // (addon-5-website_difm) is unavailable once a selected Center item already
  // has it checked as its free entitlement
  const hasWebsiteEntitlement = items.some(item =>
    item.addons?.some(addon => addon.productCode === WEBSITE_ENTITLEMENT_CODE),
  )
  if (hasWebsiteEntitlement) {
    codes.add(WEBSITE_ADDON_CODE)
  }

  // Reverse case: once the standalone add-on is already in the cart, disable
  // the ent-5-website checkbox on any Center's ProductPlanSelector panel
  // An account-owned (alreadyIncluded) standalone add-on doesn't count here:
  // buying a plan that bundles the entitlement (e.g. Signature) cancels it and
  // replaces it with the included entitlement — see Rule 1e in
  // resolveItemsAfterAdd.
  const hasWebsiteAddon = items.some(
    item => item.productCode === WEBSITE_ADDON_CODE && !item.alreadyIncluded,
  )
  if (hasWebsiteAddon) {
    codes.add(WEBSITE_ENTITLEMENT_CODE)
  }

  // Rule: Thryv Social Posts — the standalone addon is redundant (and so
  // disabled) once BC Unlimited is in the cart, since BC Unlimited already
  // bundles the same feature as a free entitlement.
  const hasBcUnlimited = items.some(
    item => item.productCode === BC_UNLIMITED_CODE,
  )
  if (hasBcUnlimited) {
    codes.add(SOCIAL_CONTENT_GEN_ADDON_CODE)
  }

  // Rule: requires / requiresCenter / requiresAddon
  // If a plan's dependencies are not yet in the cart, gray it out. "requires"
  // is OR semantics (any one code satisfies it) and also matches codes
  // nested in a Center's checked entitlements (e.g. thryv5page's requires
  // includes ent-5-website, which only ever exists inside a Center's addons,
  // never as its own top-level cart item).
  if (allPlans.length > 0) {
    const selectedCodes = new Set(
      items.flatMap(item => [
        item.productCode,
        ...(item.addons?.map(addon => addon.productCode ?? addon.code) ?? []),
      ]),
    )
    allPlans.forEach(plan => {
      const required = [
        ...(plan.requires || []),
        ...(plan.requiresCenter || []),
        ...(plan.requiresAddon || []),
      ]

      if (
        required.length > 0 &&
        !required.some(code => selectedCodes.has(code))
      ) {
        codes.add(plan.code)
      }
    })
  }

  // Rule: upgradeGated
  // Mirrors the upgradeGated block in centers-and-apps.js. Only active when
  // isUserEnabledForMCFreeTrial is on. Plans are disabled unless the cart or
  // account context satisfies the show/hide conditions.
  const {
    selectedCountry,
    allAccountSubs = [],
    isUserEnabledForMCFreeTrial,
    // hasBusinessCenterAccess/hasMarketingCenterAccess default open (true) —
    // computed booleans whose own formula already returns true once no
    // pilot/restriction flag is active, unlike the
    // three raw flags below, which are read directly off userFeatures with
    // no fallback in the legacy code, so an unloaded/absent flag is
    // restrictive there too (falsy → disabled) — default false to match.
    hasBusinessCenterAccess = true,
    hasMarketingCenterAccess = true,
    hasKeapMaxAccess = false,
    hasKeapHVACAccess = false,
    hasCustomDataFYAccess = false,
    isNewTLMCAccessible = false,
    isSocialAdsAndTLWithBCEnabled = false,
    isUserEnabledForMCAU = false,
    isUserEnabledForMCNZ = false,
    isTgpDowngradeEnabled = false,
    isStarterSocialAdsEnabled = false,
  } = accountContext

  // Rule: GEP-7133 / FULSAS-13599 - TGP Starter is Foundation-only. Per the
  // epic's catalog mapping (MC Foundation -> Starter/Signature/Signature
  // Plus; MC Plus/Pro -> Signature/Signature Plus only), Starter is not a
  // valid upgrade target for an MC Plus/Pro/Pro Enh account — disable it
  // whenever the account's existing MC 5.0 plan is anything other than
  // Foundation. Only active in the TGP upgrade flow (Enable_TGP_Upgrade), and
  // only while Enable_TGP_Downgrade is off — that's the permission flag
  // (mirrors MC_Downgrade_Access) that explicitly grants this downgrade, at
  // which point Starter must stay selectable. TO REVERT: delete this block.
  if (!isTgpDowngradeEnabled) {
    const hasNonFoundationMc5Plan = allAccountSubs.some(sub =>
      [...MC_PLANS_CODE, 'mc_pro_enh-5-thryv-m2m'].includes(
        getSubPlanCode(sub),
      ),
    )
    if (hasNonFoundationMc5Plan) {
      ;['mc_starter-5-thryv-m2m', 'starter-5-thryv-annual'].forEach(code =>
        codes.add(code),
      )
    }
  }

  // Rule: FULSAS-13789 - TGP Foundation and Thryv Basic are not applicable
  // when a legacy Marketing Center (Plus/Pro/Pro Enh) or Business Center plan
  // is already on the account or in the cart. A plan that is itself already
  // in the cart/account (e.g. an auto-added MC Foundation) stays enabled.
  const hasLegacyMcOrBc =
    items.some(item => isTgpFoundationBlockingCode(item.productCode)) ||
    allAccountSubs.some(sub => isTgpFoundationBlockingCode(getSubPlanCode(sub)))
  if (hasLegacyMcOrBc) {
    ;[MC_FOUNDATION_CODE, ...THRYV_BASIC_CODES].forEach(code => {
      if (!items.some(item => item.productCode === code)) codes.add(code)
    })
  }

  // Rule: FULSAS-13789 - Growth Packages cannot be added while TGP Foundation
  // or Thryv Basic is on the account or in the cart.

  const hasTgpFoundationFamily =
    items.some(item => isTgpFoundationFamilyCode(item.productCode)) ??
    allAccountSubs.some(sub => isTgpFoundationFamilyCode(getSubPlanCode(sub)))
  if (hasTgpFoundationFamily) {
    GROWTH_PACKAGES.forEach(code => {
      if (!items.some(item => item.productCode === code)) codes.add(code)
    })
  }

  // Rule: Professionally Designed Website — not available for NEW clients.
  // Proposals with clientType 'NEW' have no existing account to provision a
  // website against, so the add-on must remain off-limits.
  const {clientType} = accountContext
  if (clientType === 'NEW') {
    codes.add(WEBSITE_ADDON_CODE)
  }

  // Rule: Professionally Designed Website — disabled when ent-5-website is
  // present directly on the account as a standalone subscription. The existing
  // hasWebsiteEntitlement rule above already covers ent-5-website nested as an
  // addon inside a proposal-option item (including alreadyIncluded account
  // items). This catches any remaining case where it appears as a top-level
  // childSubscription on the account.
  const hasWebsiteEntitlementOnAccount = allAccountSubs.some(
    sub => getSubPlanCode(sub) === WEBSITE_ENTITLEMENT_CODE,
  )
  if (hasWebsiteEntitlementOnAccount) {
    codes.add(WEBSITE_ADDON_CODE)
  }

  // Rule: Professionally Designed Website — disabled when any of the blocked
  // MC 2.0 plans are present either in the current proposal option or on the
  // existing account.
  const hasBlockingMCPlan =
    items.some(item =>
      WEBSITE_ADDON_BLOCKED_BY_CODES.includes(item.productCode),
    ) ||
    allAccountSubs.some(sub =>
      WEBSITE_ADDON_BLOCKED_BY_CODES.includes(getSubPlanCode(sub)),
    )
  if (hasBlockingMCPlan) {
    codes.add(WEBSITE_ADDON_CODE)
  }

  // Rule: Thryv 2.0 Growth Platform — 5 Extra Website Pages (thryv5page) is
  // only selectable for Growth Platform accounts that already own
  // Professionally Designed Website.
  const hasGrowthPlatformPlan = allAccountSubs.some(sub =>
    MC_2_0_PLANS.includes(getSubPlanCode(sub)),
  )
  if (hasGrowthPlatformPlan && selectedCountry === 'US') {
    const hasProfessionallyDesignedWebsite =
      allAccountSubs.some(
        sub =>
          getSubPlanCode(sub) === WEBSITE_ADDON_CODE ||
          (sub.addOns ?? []).some(addon =>
            [WEBSITE_ADDON_CODE, WEBSITE_ENTITLEMENT_CODE].includes(
              addon.addOn?.code,
            ),
          ),
      ) ||
      items.some(item =>
        (item.addons ?? []).some(addon =>
          [WEBSITE_ADDON_CODE, WEBSITE_ENTITLEMENT_CODE].includes(
            addon.productCode ?? addon.code,
          ),
        ),
      )
    if (hasProfessionallyDesignedWebsite) {
      codes.delete('thryv5page')
    } else {
      codes.add('thryv5page')
    }
  }

  // Rule: AU/NZ — disable MC 2.0 plans that would be a downgrade from the
  // account's existing MC 2.0 plan. Mirrors centers-and-apps.js:680-690.
  const isAUNZMC20Flag =
    (selectedCountry === 'AU' && isUserEnabledForMCAU) ||
    (selectedCountry === 'NZ' && isUserEnabledForMCNZ)
  if (isAUNZMC20Flag && allPlans.length > 0) {
    const existingMC20PlanCode = allAccountSubs
      .map(getSubPlanCode)
      .find(code => MC_2_0_PLANS.includes(code))
    const existingMC20Plan = allPlans.find(p => p.code === existingMC20PlanCode)
    if (existingMC20Plan) {
      allPlans.forEach(plan => {
        if (
          MC_2_0_PLANS.includes(plan.code) &&
          existingMC20Plan.productRank > (plan.productRank || 0)
        ) {
          codes.add(plan.code)
        }
      })
    }
  }

  // Rule: BC_Catalog_Provisions_Pilot/Restrictions/Update_Only and
  // MC_Catalog_Provisions_Pilot/Restrictions → hasBusinessCenterAccess /
  // hasMarketingCenterAccess. Grays
  // out the whole Business/Marketing Center group for cohorts without
  // access, unless a plan from that same group is already in the cart.
  if (!hasBusinessCenterAccess || !hasMarketingCenterAccess) {
    const groupAlreadyInCart = groupName =>
      items.some(
        item => getProductConfig?.(item.productCode)?.uigroup === groupName,
      )
    const bcAlreadyInCart = groupAlreadyInCart('Business Center')
    const mcAlreadyInCart = groupAlreadyInCart('Marketing Center')
    allPlans.forEach(plan => {
      if (
        !hasBusinessCenterAccess &&
        plan.uigroup === 'Business Center' &&
        !bcAlreadyInCart
      ) {
        codes.add(plan.code)
      }
      if (
        !hasMarketingCenterAccess &&
        plan.uigroup === 'Marketing Center' &&
        !mcAlreadyInCart
      ) {
        codes.add(plan.code)
      }
    })
  }

  // Rule: KeapMax_Catalog_Provisions / ThryvHVAC_Catalog_Provisions — mirrors
  // checkIfAppIsUnavailableForRole (centers-and-apps.js:227-277). Keap Max
  // family plans are disabled without cohort access unless already in cart;
  // HVAC/Roofing/Electrical/Plumbing vertical add-ons additionally require a
  // Keap Max plan already in the cart, not just the flag.
  const hasKeapMaxInCart = items.some(item =>
    KEAP_FAMILY_CODES.includes(item.productCode),
  )
  if (!hasKeapMaxAccess && !hasKeapMaxInCart) {
    KEAP_FAMILY_CODES.forEach(code => codes.add(code))
  }
  // Rule: Business Center and Keap are mutually exclusive on one order — once
  // any BC plan is in the cart (owned or newly added) Keap plans are greyed
  // out, and once a Keap plan is in the cart BC plans are greyed out. Each
  // side is skipped when the other family is absent from the gate, i.e. an
  // account that somehow already holds both keeps switching tiers in each.
  const hasAnyBcInCart = hasBusinessCenterInItems(items)
  if (hasAnyBcInCart && !hasKeapMaxInCart) {
    KEAP_FAMILY_CODES.forEach(code => codes.add(code))
  }
  if (hasKeapMaxInCart && !hasAnyBcInCart) {
    ALL_BC_PLAN_CODES.forEach(code => codes.add(code))
  }
  if (!hasKeapHVACAccess || !hasKeapMaxInCart) {
    const hasHvacInCart = items.some(item =>
      HVAC_CODES.includes(item.productCode),
    )
    if (!hasHvacInCart) {
      HVAC_CODES.forEach(code => codes.add(code))
    }
  }

  // Rule: Custom_Data_For_You_Access.
  // The fee's own requiresCenter (Keap Max/Ultimate) already keeps it out of
  // view otherwise, so this only needs to add the flag-off case.
  if (!hasCustomDataFYAccess) {
    codes.add('addon-5-keap-custdfy-ot')
  }

  // Rule: TL_MC_Access + Enable_Social_Ads_And_TL_With_BC. Thryv Leads and Marketing Center are
  // mutually exclusive by default; isNewTLMCAccessible lifts that for both,
  // and additionally unlocks TL/Social-Ads alongside Business Center (US
  // only) when isSocialAdsAndTLWithBCEnabled is also on.
  const hasThryvLeadsInCart = items.some(item =>
    TL_CODES.includes(item.productCode),
  )
  const hasMcPlusOrProInCart = items.some(item =>
    MC_CODES_FOR_LEADS_LIFT_BNI.includes(item.productCode),
  )
  const hasMcFoundationInCart = items.some(item =>
    isTgpFoundationFamilyCode(item.productCode),
  )
  const hasMcSigPremierInCart = items.some(item =>
    MC_2_0_SIGPREMIERPLANS.includes(item.productCode),
  )
  const hasMcProEnhancedInCart = items.some(
    item => item.productCode === 'mc_pro_enh-5-thryv-m2m',
  )
  const hasPaidMcTrialInCart = items.some(item =>
    MC_2_0_PLANS.includes(item.productCode),
  )
  const hasBcInCart = items.some(item =>
    BC_5_TIER_CODES.includes(item.productCode),
  )
  const isTLAvailableWithBC =
    isSocialAdsAndTLWithBCEnabled && hasBcInCart && selectedCountry === 'US'

  if (hasThryvLeadsInCart && !isNewTLMCAccessible) {
    MC_CENTER_CODES.filter(code => code.includes('mc_')).forEach(code =>
      codes.add(code),
    )
  }
  if (isNewTLMCAccessible) {
    const isMcAvailableForTL =
      hasMcPlusOrProInCart ||
      hasMcFoundationInCart ||
      hasMcSigPremierInCart ||
      isTLAvailableWithBC
    if (!isMcAvailableForTL) {
      if (
        (!hasMcPlusOrProInCart || !hasPaidMcTrialInCart) &&
        selectedCountry === 'US' &&
        !isTLAvailableWithBC
      ) {
        TL_CODES.forEach(code => codes.add(code))
      } else if (
        (!hasMcPlusOrProInCart || !hasPaidMcTrialInCart) &&
        selectedCountry !== 'US'
      ) {
        TL_CODES.forEach(code => codes.add(code))
      }
    }
    // Only one Thryv Leads product at a time.
    if (hasThryvLeadsInCart) {
      TL_CODES.forEach(code => {
        if (!items.some(item => item.productCode === code)) codes.add(code)
      })
    }
  }

  // Social Ads (addon-socialads*).
  // Matched by code substring like the legacy check, since these are a
  // small tiered family (bronze/silver/gold) rather than fixed codes.
  const hasStarterInCart = items.some(item =>
    THRYV_STARTER_CODES.includes(item.productCode),
  )
  const isMcFamilyInCartForSocialAds =
    hasMcPlusOrProInCart ||
    hasMcFoundationInCart ||
    hasMcSigPremierInCart ||
    hasMcProEnhancedInCart ||
    (isStarterSocialAdsEnabled && hasStarterInCart)
  if (!isMcFamilyInCartForSocialAds && !isTLAvailableWithBC) {
    allPlans
      .filter(plan => plan.code?.includes('addon-socialads'))
      .forEach(plan => codes.add(plan.code))
  }
  if (allPlans.length > 0 && isUserEnabledForMCFreeTrial) {
    const cartCodes = items
      .filter(item => item.productType !== 'ONE_TIME_FEE')
      .map(item => item.productCode)
    const isUpgrade = items.some(item => item.alreadyIncluded)
    const isFreeAccount =
      allAccountSubs.length > 0 &&
      allAccountSubs.every(sub =>
        FREE_ACCOUNT_CODES.includes(getSubPlanCode(sub)),
      )
    const hasActiveMCFreeTrial = allAccountSubs.some(
      sub =>
        getSubPlanCode(sub) === 'mc_freetrial-5-thryv-m2m' &&
        sub.state === 'active',
    )

    allPlans.forEach(plan => {
      if (!plan.upgradeGated) return

      const gatedCountries = plan.upgradeGatedCountries
        ? plan.upgradeGatedCountries.split(',').map(s => s.trim())
        : []
      const showCodes = plan.showWhenCartHas
        ? plan.showWhenCartHas.split(',').map(s => s.trim())
        : []
      const hideCodes = plan.hideWhenCartHas
        ? plan.hideWhenCartHas.split(',').map(s => s.trim())
        : []
      const isCountryInScope =
        !gatedCountries.length || gatedCountries.includes(selectedCountry)

      if (isCountryInScope) {
        if (hideCodes.some(c => cartCodes.includes(c))) {
          codes.add(plan.code)
          return
        }
        if (showCodes.some(c => cartCodes.includes(c))) return
        if (!isUpgrade || isFreeAccount || hasActiveMCFreeTrial)
          codes.add(plan.code)
      }
    })
  }

  // TGP-DOWNGRADE #9 (to revert: delete this if-block and the
  // THRYV_STARTER_CODES / isTgpSignatureCode imports it needs).
  // Rule: Signature -> Starter is a downgrade. Without Enable_TGP_Downgrade,
  // Starter (monthly and annual) is greyed out once a Signature / Signature
  // Plus plan is on the account or in the cart. With the flag on, #3 below
  // keeps every TGP plan enabled instead.
  if (
    !isTgpDowngradeEnabled &&
    items.some(item => isTgpSignatureCode(item.productCode))
  ) {
    THRYV_STARTER_CODES.forEach(code => codes.add(code))
  }

  // ---- BEGIN TGP-DOWNGRADE #3 (to revert: delete up to the END marker) ----
  // Rule: Enable_TGP_Downgrade — with this permission, no rule above may grey
  // out an individual TGP plan (Starter/Signature/Signature Plus/Premier,
  // TGP Foundation, Thryv Basic). The one exception is a TGP card that is
  // disabled as a whole (every plan in its UI group is disabled, e.g. no
  // Marketing Center access or Thryv Leads exclusivity): that stays disabled.
  // Decided from a snapshot so un-greying one plan can't change another's
  // card-level result.
  if (isTgpDowngradeEnabled) {
    const disabledBefore = new Set(codes)
    disabledBefore.forEach(code => {
      if (!isTgpFamilyCode(code)) return
      const uigroup = allPlans.find(plan => plan.code === code)?.uigroup
      const cardPlans = uigroup
        ? allPlans.filter(plan => plan.uigroup === uigroup)
        : []
      const isEntireCardDisabled =
        cardPlans.length > 0 &&
        cardPlans.every(plan => disabledBefore.has(plan.code))
      if (!isEntireCardDisabled) codes.delete(code)
    })
  }
  // ---- END TGP-DOWNGRADE #3 ----

  return codes
}

// Computes all rule-derived properties that are needed on every render
// (i.e. not tied to a specific add/remove action) and when is initialized too. Call this inline in the
// hook body so the UI always reflects the latest cart state.
export function getRenderRules(
  items,
  getProductConfig,
  allPlans = [],
  accountContext = {},
) {
  return {
    disabledPlanCodes: getDisabledPlanCodes(
      items,
      getProductConfig,
      allPlans,
      accountContext,
    ),
  }
}

// Plans not priced in the selected country's currency are fully removed
// rather than just disabled — they aren't purchasable there at all, so
// they shouldn't appear in the grid to begin with. Filter
// softwareGroups/addonGroups with this before anything else (allPlans,
// disabledPlanCodes, etc.) derives from them, same place/timing as the
// requiresFlags/restrictFlags filtering in productPreloadRules.js.
export function filterGroupsByCountry(
  groups,
  country,
  userFeatures = {},
  {
    ignoreNeedFlag = false,
    allAccountSubs = [],
    isTgpUpgradeEnabled = false,
  } = {},
) {
  // KeapPartner sessions (ignoreNeedFlag = true) bypass filterGroupsByAccountContext,
  // so Signature/TGP plans must be hidden here when the account already owns a
  // legacy MC 5.0 Plus or Pro plan and no TGP upgrade path is enabled.
  // Scoped to MC_PLANS_CODE only (mc_plus / mc_pro) — Foundation and new/empty
  // accounts must still see both MC and Thryv Growth Platform plans.
  const shouldHideSignaturePlans =
    ignoreNeedFlag &&
    !isTgpUpgradeEnabled &&
    allAccountSubs.some(sub => MC_PLANS_CODE.includes(getSubPlanCode(sub)))

  return groups
    .map(group => ({
      ...group,
      plans: (group.plans ?? []).filter(plan => {
        if (shouldHideSignaturePlans && isTgpSignatureCode(plan.code))
          return false
        return isPlanAllowedForCountry(
          plan,
          country,
          userFeatures,
          ignoreNeedFlag,
        )
      }),
    }))
    .filter(group => group.plans.length > 0)
}

// Mirrors the mc_pro/mc_plus visibility gate in centers-and-apps.js
// lines 1333–1356. PartnerOps bypass is omitted — not applicable in
// the Proposal Tool. Non-US accounts always see these plans.
export function filterGroupsByAccountContext(groups, accountContext = {}) {
  const {
    selectedCountry,
    allAccountSubs = [],
    isUserEnabledForMCFreeTrial,
    // Raw flag read directly off userFeatures with no fallback in the legacy
    // code (centers-and-apps.js:975) — an unloaded/absent flag is falsy
    // there too, so default false (restrictive) to match.
    isUserEnabledForBC5NewSales = false,
    isUserEnabledForMCAU = false,
    isUserEnabledForMCNZ = false,
    // GEP-7133 / FULSAS-13599 (MC 5.0 -> TGP upgrade): see the "MC 5.0 -> TGP
    // upsell visibility override" doc comment below, right before the
    // MC_2_0_PLANS block, for what this does and how to revert it. Mirrors
    // the same override/flag in centers-and-apps.js.
    isTgpUpgradeEnabled = false,
    isPartnerOpsUser = false,
    // Exact match on centers-and-apps.js:1478's `roles.length === 1 &&
    // roles[0] === 'PartnerOps'` — distinct from isPartnerOpsUser above,
    // which also covers accounts with PartnerOps plus other roles.
    isSuccessServicesRestrictedForPartnerOps = false,
    // Not currently sourced anywhere in the Proposal Tool/CatalogSelection
    // flows (no equivalent of the legacy order.template/industryId field
    // exists there yet) — this filter is a no-op (both HIPAA and Restricted
    // Access stay visible) until a caller supplies it.
    industryTemplate,
    // Cart state (not an account-owned sub) — mirrors centers-and-apps.js's
    // selectedCentersAndApps-sourced hasKeapMax, distinct from hasBC5Plan
    // below which is account-owned (allAccountSubs/defaultCentersAndApps).
    hasKeapMaxInCart = false,
    // Account-owned subscription items (selectRecurlySubscriptionItems).
    // Used to gate mc_pro_enh visibility.
    accountItems = [],
    // FULSAS-13789: TGP Foundation (mc_foundation) and Thryv Basic are shown
    // only when the user holds the matching feature flag.
    isTgpFoundationCatalogEnabled = false,
    isThryvBasicCatalogEnabled = false,
    // Cart items — used to check if ent-5-website is in the cart as an
    // entitlement (e.g. via Signature+) so thryv5page can be shown even
    // when the account doesn't already own Professionally Designed Website.
    cartItems = [],
  } = accountContext

  const hasMCTrialPlan = allAccountSubs.some(sub =>
    MC_TRIAL_PLAN_CODES.includes(getSubPlanCode(sub)),
  )
  const hadInactiveMCFreeTrial = allAccountSubs.some(
    sub =>
      getSubPlanCode(sub) === 'mc_freetrial-5-thryv-m2m' &&
      sub.state !== 'active',
  )
  const hasMC2PlanExisting = allAccountSubs.some(sub =>
    MC_2_0_PLANS.includes(getSubPlanCode(sub)),
  )
  // Account already owns Professionally Designed Website
  const hasProfessionallyDesignedWebsite = allAccountSubs.some(
    sub =>
      getSubPlanCode(sub) === WEBSITE_ADDON_CODE ||
      (sub.addOns ?? []).some(addon =>
        [WEBSITE_ADDON_CODE, WEBSITE_ENTITLEMENT_CODE].includes(
          addon.addOn?.code,
        ),
      ),
  )
  const isFreeAccount = allAccountSubs.every(sub =>
    FREE_ACCOUNT_CODES.includes(getSubPlanCode(sub)),
  )
  const isHipaaCompliantIndustry =
    industryTemplate != null &&
    HIPAA_COMPLIANT_INDUSTRIES.includes(industryTemplate)
  const hasBC5Plan = allAccountSubs.some(sub =>
    BC_5_TIER_CODES.includes(getSubPlanCode(sub)),
  )

  // Account owns mc_pro_enh — used to gate mc_pro_enh visibility and hide mc_plus/mc_pro.
  // Mirrors centers-and-apps.js:1311-1313 (hasMCProEnhanced from defaultCentersAndApps.centers).
  const hasMCProEnhanced = accountItems.some(
    item => item.productCode === 'mc_pro_enh-5-thryv-m2m',
  )
  // GEP-7133 / FULSAS-13599: account already owns an MC 5.0 source plan
  // (Foundation/Plus/Pro) — used only by the TGP upsell override
  // below. Mirrors the `hasMc5PlanAlreadyIncluded` check in
  // centers-and-apps.js's MC_2_0_PLANS block.
  const hasMc5PlanAlreadyIncluded = accountItems.some(item =>
    [
      ...MC_PLANS_CODE,
      // 'mc_pro_enh-5-thryv-m2m',
      'mc_foundation-5-thryv-m2m',
    ].includes(item.productCode),
  )

  return groups
    .map(group => {
      // const isMarketingCenterGroup = group.name
      //   ?.toLowerCase()
      //   .includes('marketing center')
      if (group.name === 'Thryv Success Services') {
        return {
          ...group,
          plans: isSuccessServicesRestrictedForPartnerOps ? [] : group.plans,
        }
      }
      return {
        ...group,
        plans: (group.plans ?? []).filter(plan => {
          if (industryTemplate != null) {
            if (plan.code === HIPAA_ADDON_CODE) return isHipaaCompliantIndustry
            if (plan.code === RESTRICTED_ACCESS_CODE) {
              return (
                !isHipaaCompliantIndustry ||
                RESTRICTED_ACCESS_COUNTRY_EXCEPTIONS.includes(selectedCountry)
              )
            }
          }
          if (BC_5_TIER_CODES.includes(plan.code)) {
            return isUserEnabledForBC5NewSales || hasBC5Plan
          }
          // bc-6-thryv-m2m is the standalone BC fallback — only shown once
          // neither a BC 5.0 tier nor a Keap Max plan is already present.
          if (plan.code === BC_6_CODE) {
            return !(hasBC5Plan || hasKeapMaxInCart)
          }
          // Hidden unless account already owns it (legacy upgrade-only product).
          // Mirrors centers-and-apps.js:1331-1333.
          if (plan.code === 'mc_pro_enh-5-thryv-m2m') return hasMCProEnhanced
          // FULSAS-13789: TGP Foundation / Thryv Basic are feature-flag gated.
          // Mirrors centers-and-apps.js.
          if (plan.code === MC_FOUNDATION_CODE)
            return isTgpFoundationCatalogEnabled
          if (isThryvBasicCode(plan.code)) return isThryvBasicCatalogEnabled
          // Thryv 2.0 Growth Platform — 5 Extra Website Pages (thryv5page) is hidden entirely
          if (
            plan.code === 'thryv5page' &&
            hasMC2PlanExisting &&
            selectedCountry === 'US'
          ) {
            const hasPDWInCart = cartItems.some(item =>
              (item.addons ?? []).some(addon =>
                [WEBSITE_ADDON_CODE, WEBSITE_ENTITLEMENT_CODE].includes(
                  addon.productCode ?? addon.code,
                ),
              ),
            )
            return hasProfessionallyDesignedWebsite || hasPDWInCart
          }
          // MC plan visibility rules apply regardless of which catalog group
          // the plan lives in — annual MC 2.0 plans are in "Thryv Growth
          // Platform" (uigroup), not "Marketing Center", so checking
          // isMarketingCenterGroup first would bypass these gates entirely.
          // Moved before the !isMarketingCenterGroup early return so both
          // the "Marketing Center" (M2M) and "Thryv Growth Platform" (annual)
          // groups are subject to the same account-context rules.
          if (MC_PLANS_CODE.includes(plan.code)) {
            if (hasMCProEnhanced) return false
            const isAUNZMC20Flag =
              (selectedCountry === 'AU' && isUserEnabledForMCAU) ||
              (selectedCountry === 'NZ' && isUserEnabledForMCNZ)
            console.log('isAUNZMC20Flag' + isAUNZMC20Flag)
            console.log('hasMC2PlanExisting' + hasMC2PlanExisting)
            console.log('selectedCountry' + selectedCountry)
            console.log(
              'isUserEnabledForMCFreeTrial' + isUserEnabledForMCFreeTrial,
            )
            console.log('isFreeAccount' + isFreeAccount)
            if (isAUNZMC20Flag) {
              // GEP-7133 / FULSAS-13599: AU/NZ Marketing Center visibility is
              // independent of MC_Free_Trial — that flag is US-only. New-flow
              // (genuinely free/new) AU/NZ accounts never see legacy MC once
              // MC_AU/MC_NZ is on; upgrade-flow (existing paid) AU/NZ
              // accounts keep the original, unmodified behavior.
              if (isFreeAccount) return false
              return !hasMC2PlanExisting
            }
            if (selectedCountry !== 'US') {
              if (hasMC2PlanExisting) return false
              return true
            } else {
              if (isUserEnabledForMCFreeTrial) {
                if (isPartnerOpsUser) {
                  if (hasMC2PlanExisting) return false
                  return true
                }
                if (hasMCTrialPlan || hadInactiveMCFreeTrial) {
                  return false
                }
                if (isFreeAccount) {
                  return false
                }
              } else {
                if (hasMC2PlanExisting) return false
                return true
              }
            }
          }
          if (MC_2_0_PLANS.includes(plan.code)) {
            if (hasMCProEnhanced) return false
            // ---- BEGIN GEP-7133 / FULSAS-13599: MC 5.0 -> TGP upsell visibility override ----
            // WHY: every branch below this point either requires the account
            // to already own a TGP/MC-2.0 plan, be free/trial, or be net-new
            // — it never shows TGP just because the account already has a
            // paid MC 5.0 plan (Foundation/Plus/Pro). That's by design for
            // FULSAS-10255's GA rollout, but it's the opposite of what
            // GEP-7133 needs: letting a rep upsell an *existing* MC 5.0
            // account to TGP.
            // WHAT: when the ENABLE_TGP_Upgrade feature-table flag is on and
            // the account already owns an MC 5.0 plan, show TGP regardless of
            // the country/free/trial checks below.
            // TO REVERT: delete this `if (isTgpUpgradeEnabled && ...)` block
            // (and, if nothing else uses them, the `isTgpUpgradeEnabled`
            // param above and `hasMc5PlanAlreadyIncluded` const above that) —
            // everything below it is untouched and works standalone.
            if (isTgpUpgradeEnabled && hasMc5PlanAlreadyIncluded) return true
            // ---- END GEP-7133 / FULSAS-13599 override ----
            // AU/NZ: always show MC 2.0 plans when the country-specific flag
            // is enabled — mirrors centers-and-apps.js:680-690 which shows
            // all plans and only disables those that would be a downgrade.
            // Downgrade candidates are grayed out via getDisabledPlanCodes,
            // not hidden here.
            if (
              (selectedCountry === 'AU' && isUserEnabledForMCAU) ||
              (selectedCountry === 'NZ' && isUserEnabledForMCNZ)
            ) {
              return true
            }
            if (selectedCountry !== 'US') {
              return false
            } else {
              if (isUserEnabledForMCFreeTrial) {
                if (
                  !(hasMCTrialPlan || hadInactiveMCFreeTrial) &&
                  !isFreeAccount
                )
                  return false
                else return true
              } else {
                return false
              }
            }
          }
          return true
        }),
      }
    })
    .filter(group => group.plans.length > 0)
}

// accountItems: original Recurly subscription items for the account (upgrade
// path only). When provided, downgrade/restore rules activate: removing an
// upgraded plan restores the account's lower-tier plan instead of deleting it.
// autoAddCenters/disableApps/disableCenters are looked up via getProductConfig
// rather than read off stored items, since normalized items (toApiItem) don't
// carry those catalog-only fields.

// Removes items whose requires/requiresCenter/requiresAddon dependencies are
// no longer satisfied by what's currently in the cart — e.g. thryv5page's
// requires (addon-5-website_difm, bc-6-thryv-m2m, ent-5-website) once none of
// those remain, whether the trigger was removed directly or was a nested
// entitlement inside a Center that just got removed. Checked against every
// item on every pass (cheap, and correct regardless of *why* something
// disappeared) rather than tracking which codes were just removed — matches
// getDisabledPlanCodes' flattening of item.addons so a checked entitlement
// counts the same as a top-level product code.
function cascadeRequiresRemoval(result, getProductConfig) {
  const availableCodes = new Set(
    result.flatMap(item => [
      item.productCode,
      ...(item.addons?.map(addon => addon.productCode ?? addon.code) ?? []),
    ]),
  )
  return result.filter(item => {
    if (item.alreadyIncluded) return true
    const config = getProductConfig?.(item.productCode) ?? null
    const required = [
      ...(config?.requiresCenter ?? []),
      ...(config?.requires ?? []),
      ...(config?.requiresAddon ?? []),
    ]
    if (required.length === 0) return true
    return required.some(code => availableCodes.has(code))
  })
}

export function resolveItemsAfterRemove(
  items,
  productCode,
  getProductConfig,
  {accountItems = [], selectedCountry, getAutoAddItemConfig} = {},
) {
  const removedItem = items.find(item => item.productCode === productCode)
  const isUpgrade = accountItems.length > 0

  // Helpers to look up account items by productCode
  const findInAccount = (...codes) =>
    accountItems.find(i => codes.includes(i.productCode))
  const accountHas = code => Boolean(findInAccount(code))

  // --- Rule 1: Remove or downgrade the item ---
  // In the upgrade path, removing a plan may restore the account's lower-tier
  // plan instead of deleting the slot entirely (mirrors catalog.js handleRemoveItem).
  let result = items
  let wasReplaced = false

  if (isUpgrade) {
    if (
      productCode === 'mc_pro-5-thryv-m2m' &&
      accountHas('mc_plus-5-thryv-m2m')
    ) {
      result = items.map(i =>
        i.productCode === productCode
          ? findInAccount('mc_plus-5-thryv-m2m')
          : i,
      )
      wasReplaced = true
    } else if (
      productCode === 'bc_pro-5-thryv-m2m' &&
      accountHas('bc_plus-5-thryv-m2m')
    ) {
      result = items.map(i =>
        i.productCode === productCode
          ? findInAccount('bc_plus-5-thryv-m2m')
          : i,
      )
      wasReplaced = true
    } else if (
      productCode === 'bc_unlm-5-thryv-m2m' &&
      accountHas('bc_plus-5-thryv-m2m')
    ) {
      result = items.map(i =>
        i.productCode === productCode
          ? findInAccount('bc_plus-5-thryv-m2m')
          : i,
      )
      wasReplaced = true
    } else if (
      productCode === 'bc_unlm-5-thryv-m2m' &&
      accountHas('bc_pro-5-thryv-m2m')
    ) {
      result = items.map(i =>
        i.productCode === productCode ? findInAccount('bc_pro-5-thryv-m2m') : i,
      )
      wasReplaced = true
    } else if (
      productCode === 'keap_kcp-cert-5-fee-annual' ||
      productCode === 'keap_kcc-cert-5-fee-annual'
    ) {
      const accountVersion = findInAccount(productCode)
      if (accountVersion) {
        result = items.map(i =>
          i.productCode === productCode ? accountVersion : i,
        )
        wasReplaced = true
      }
    } else if (
      productCode === 'addon-5-mcexpangr' &&
      accountHas('addon-5-mcstartgr')
    ) {
      result = items.map(i =>
        i.productCode === productCode ? findInAccount('addon-5-mcstartgr') : i,
      )
      wasReplaced = true
    } else if (RESTORE_OR_REMOVE_CODES.includes(productCode)) {
      const accountVersion = findInAccount(productCode)
      if (accountVersion) {
        result = items.map(i =>
          i.productCode === productCode ? accountVersion : i,
        )
        wasReplaced = true
      }
    } else if (isTgpFoundationFamilyCode(productCode)) {
      const accountMC20Plan = accountItems.find(i =>
        MC_2_0_PLANS.includes(i.productCode),
      )
      if (accountMC20Plan) {
        result = [
          ...result.filter(i => i.productCode !== productCode),
          accountMC20Plan,
        ]
        wasReplaced = true
      }
    } else if (MC_2_0_PLANS.includes(productCode)) {
      // GEP-7133 / FULSAS-13599: undo resolveItemsAfterAdd's TGP -> MC 5.0
      // displacement (search "GEP-7133" there) — when the TGP plan is
      // removed, restore whichever legacy MC 5.0 plan (Foundation/Plus/Pro/
      // Pro Enh) the account originally had, exactly like the MC_FOUNDATION_CODE
      // branch above does in the opposite direction. TO REVERT: delete this
      // branch; it's independent of every other branch in this chain.
      const accountMC5Plan = findInAccount(
        ...MC_PLANS_CODE,
        'mc_pro_enh-5-thryv-m2m',
        MC_FOUNDATION_CODE,
        ...THRYV_BASIC_CODES,
      )
      if (accountMC5Plan) {
        result = [
          ...result.filter(i => i.productCode !== productCode),
          accountMC5Plan,
        ]
        wasReplaced = true
      }
    } else if (productCode === 'seobase-5-thryv-m2m') {
      const accountVersion = findInAccount(productCode)
      if (accountVersion) {
        result = items.map(i =>
          i.productCode === productCode ? accountVersion : i,
        )
        wasReplaced = true
      }
    } else if (productCode === 'addon-5-stratcontent-m2m') {
      const accountListingRefresh = findInAccount(...LISTING_REFRESH_CODES)
      if (accountListingRefresh) {
        result = items.map(i =>
          i.productCode === productCode ? accountListingRefresh : i,
        )
        wasReplaced = true
      }
    } else if (KEAP_FAMILY_M2M_CODES.includes(productCode)) {
      // Restore the account's owned Keap plan: a legacy one, or a current one
      // (e.g. owned Keap Max when Keap Ultimate is removed after an upgrade).
      const ownedKeapM2m = accountItems.find(
        i =>
          i.productCode !== productCode &&
          (KEAP_FAMILY_LEGACY_CODES.includes(i.productCode) ||
            KEAP_FAMILY_M2M_CODES.includes(i.productCode)),
      )
      if (ownedKeapM2m) {
        result = items.map(i =>
          i.productCode === productCode ? ownedKeapM2m : i,
        )
        wasReplaced = true
      }
    } else if (KEAP_FAMILY_ANNUAL_CODES.includes(productCode)) {
      const ownedKeapAnnual = accountItems.find(
        i =>
          i.productCode !== productCode &&
          (KEAP_FAMILY_LEGACY_ANNUAL_CODES.includes(i.productCode) ||
            KEAP_FAMILY_ANNUAL_CODES.includes(i.productCode)),
      )
      if (ownedKeapAnnual) {
        result = items.map(i =>
          i.productCode === productCode ? ownedKeapAnnual : i,
        )
        wasReplaced = true
      }
    }
  }

  if (!wasReplaced) {
    result = result.filter(item => item.productCode !== productCode)
  }

  // --- Rule 2: Remove auto-added child centers that are no longer referenced ---
  // Never removes account items (alreadyIncluded).
  const removedProductConfig = getProductConfig?.(productCode) ?? null
  const autoAddCenters = removedProductConfig?.autoAddCenters ?? []
  if (!wasReplaced && autoAddCenters.length) {
    const removedCenterCodes = new Set(autoAddCenters.map(c => c.code))
    const stillReferencedCodes = new Set(
      result.flatMap(item => {
        const config = getProductConfig?.(item.productCode) ?? null
        return (config?.autoAddCenters ?? []).map(c => c.code)
      }),
    )
    result = result.filter(
      item =>
        !removedCenterCodes.has(item.productCode) ||
        stillReferencedCodes.has(item.productCode) ||
        item.alreadyIncluded,
    )
  }

  // --- Rule 3: MC removal/downgrade cascade ---
  // When any MC plan is removed (or downgraded), also remove newly-added SEO
  // and Thryv Leads products, plus Listing Refresh (Thryv Success Service) —
  // it's only auto-added alongside a new MC in the first place, so it no
  // longer applies once that MC is gone. Account-owned items
  // (alreadyIncluded) are kept.
  if (MC_CENTER_CODES.includes(productCode)) {
    result = result.filter(
      item =>
        !MC_SEO_TL_CASCADE_CODES.includes(item.productCode) ||
        item.alreadyIncluded,
    )

    // Rule 3b: BC6 standalone — when no MC and no other BC remain, Listing
    // Refresh no longer applies (newly-added ones only).
    const noOtherMC = !result.some(item =>
      MC_CENTER_CODES.includes(item.productCode),
    )
    const noOtherBC = !result.some(item =>
      OTHER_BC_CODES.includes(item.productCode),
    )
    if (
      result.some(item => item.productCode === BC_6_CODE) &&
      noOtherMC &&
      noOtherBC
    ) {
      result = result.filter(
        item =>
          !LISTING_REFRESH_CODES.includes(item.productCode) ||
          item.alreadyIncluded,
      )
    }
  }

  // --- Rule 4: HVAC removal cascade ---
  // When no HVAC product remains, remove newly-added vertical clientsvcs.
  if (HVAC_CODES.includes(productCode)) {
    const remainingHvac = result.filter(item =>
      HVAC_CODES.includes(item.productCode),
    )
    if (remainingHvac.length === 0) {
      result = result.filter(
        item =>
          !VERTICAL_CLIENTSVCS_CODES.includes(item.productCode) ||
          item.alreadyIncluded,
      )
    }
  }

  // --- Rule 4b: HVAC vertical re-evaluation on MC / Website removal ---
  // Mirrors the live-recalculation that legacy options.js performs on every
  // handleContinueClick: when MC Plus/Pro or a website product is removed
  // from a cart that still contains HVAC, the vertical clientsvcs code must
  // be downgraded to match the updated cart state (e.g. removing MC Plus
  // reverts vert_mc_clientsvcs → vert_clientsvcs).
  // swapHvacVertical is a no-op when no non-account vertical is present or
  // when the code already matches, so this is always safe to call.
  const isMcOrWebRemoved =
    productCode === 'mc_plus-5-thryv-m2m' ||
    productCode === 'mc_pro-5-thryv-m2m' ||
    productCode === WEBSITE_ADDON_CODE
  const hasHvacAfterRemove = result.some(item =>
    HVAC_CODES.includes(item.productCode),
  )

  if (isMcOrWebRemoved && hasHvacAfterRemove) {
    result = swapHvacVertical(
      result,
      getAutoAddItemConfig ?? getProductConfig,
      selectedCountry,
    )
  }

  // --- Rule 5: KEAP family removal cascade ---
  // Remove newly-added vertical clientsvcs products and the Keap Add-Ons
  // wrapper when no Keap center remains in the cart.
  if (KEAP_FAMILY_CODES.includes(productCode)) {
    result = result.filter(
      item =>
        !VERTICAL_CLIENTSVCS_CODES.includes(item.productCode) ||
        item.alreadyIncluded,
    )
    const hasRemainingKeapCenter = result.some(item =>
      KEAP_FAMILY_CODES.includes(item.productCode),
    )
    if (!hasRemainingKeapCenter) {
      result = result.filter(
        item =>
          item.productCode !== 'addon-5-keap-addons' &&
          item.productCode !== 'addon-5-keap-addons-annual',
      )
    }
  }

  // --- Rule 6: Restore products deactivated by the removed item (upgrade path) ---
  // If the removed item's catalog config had disableCenters/disableApps,
  // re-add any of those that exist on the original account subscription.
  if (isUpgrade && removedItem) {
    const deactivatedCodes = [
      ...(removedProductConfig?.disableCenters ?? []),
      ...(removedProductConfig?.disableApps ?? []),
    ]
    deactivatedCodes.forEach(code => {
      const accountVersion = findInAccount(code)
      const alreadyInResult = result.some(i => i.productCode === code)
      if (accountVersion && !alreadyInResult) {
        result = [...result, accountVersion]
      }
    })
  }

  // --- Rule 7: requires/requiresCenter/requiresAddon cascade removal ---
  // If the removed product (or a nested entitlement it carried) was what
  // made another item visible/selectable, remove that item too — e.g.
  // removing Business Center or Professionally Designed Website auto-removes
  // 5 Extra Website Pages if no other trigger remains.
  result = cascadeRequiresRemoval(result, getProductConfig)

  // --- Rule 8: Website Design Fee cleanup ---
  // If neither trigger for the fee remains (standalone add-on, or a Center's
  // checked ent-5-website entitlement), drop the now-orphaned fee too.
  if (
    result.some(item => item.productCode === WEBSITE_DESIGN_FEE_CODE) &&
    !hasWebsiteTrigger(result)
  ) {
    result = result.filter(
      item =>
        item.productCode !== WEBSITE_DESIGN_FEE_CODE || item.alreadyIncluded,
    )
  }

  return result
}

// Insert/update/remove a quantity-driven item (e.g. a stepper addon) by
// productCode. Used by the dedicated onQuantityChange flow — separate from
// resolveItemsAfterAdd since quantity items don't go through group
// replace/coexist rules, they just track their own count.
export function applyQuantityToItems(items, newItem) {
  const apiItem = toApiItem(newItem)

  const withoutExisting = items.filter(
    item => item.productCode !== apiItem.productCode,
  )
  return apiItem.quantity > 0 ? [...withoutExisting, apiItem] : withoutExisting
}

export function resolveItemsAfterAdd(
  items,
  newItem,
  {
    groupPlanCodes = [],
    groupId,
    selectedCountry,
    getProductConfig,
    getAutoAddItemConfig,
  } = {},
) {
  const productCode = newItem.productCode ?? newItem.productId

  // --- Guard: max quantity ---
  const maxQty = Number(newItem.maxQty)
  const limit = Number.isFinite(maxQty) && maxQty > 0 ? maxQty : 1
  const existingCount = items.filter(
    item => item.productCode === productCode,
  ).length
  if (existingCount >= limit) return items

  // --- Guard: Growth Package downgrade block ---
  // Refuse to add Starter Growth Package back while Expanded is an
  // account-owned (alreadyIncluded) upgrade item — no downgrading. Mirrors
  // the getDisabledPlanCodes gate that keeps this from being clickable.
  if (
    productCode === GROWTH_PACKAGE_STARTER &&
    items.some(
      item =>
        item.productCode === GROWTH_PACKAGE_EXPANDED && item.alreadyIncluded,
    )
  ) {
    return items
  }

  // --- Guard: Thryv Success Service downgrade block ---
  // Refuse to add Listing Refresh back while Strategic Content is an
  // account-owned (alreadyIncluded) upgrade item — no downgrading. Mirrors
  // the Growth Package guard above and the getDisabledPlanCodes gate that
  // keeps this from being clickable.
  if (
    productCode === LISTING_REFRESH_M2M_CODE &&
    items.some(
      item =>
        item.productCode === STRATEGIC_CONTENT_CODE && item.alreadyIncluded,
    )
  ) {
    return items
  }

  // --- Guard: BC 5.0 tier downgrade block ---
  // Refuse to add a lower BC tier while a higher one is account-owned
  // (alreadyIncluded). Mirrors the Growth Package guard above.
  const newBcTierIndex = BC_5_TIER_CODES.indexOf(productCode)
  if (newBcTierIndex !== -1) {
    const ownedBcIdx = BC_5_TIER_CODES.reduce(
      (maxIdx, code, idx) =>
        items.some(item => item.productCode === code && item.alreadyIncluded)
          ? idx
          : maxIdx,
      -1,
    )
    if (ownedBcIdx > newBcTierIndex) return items
    // FULSAS-15052: an owned BC 6.0 / Enterprise plan outranks (or duplicates)
    // every BC 5.0 tier — same gate as getDisabledPlanCodes.
    if (
      items.some(
        item =>
          BC_OWNED_BLOCKS_ALL_5_TIER_CODES.includes(item.productCode) &&
          item.alreadyIncluded,
      )
    ) {
      return items
    }
  }

  // --- Guard: Business Center and Keap are mutually exclusive ---
  // Mirrors the getDisabledPlanCodes gate that keeps the other family from
  // being clickable. An account already holding both keeps tier switching.
  if (
    KEAP_FAMILY_CODES.includes(productCode) &&
    !hasKeapInItems(items) &&
    hasBusinessCenterInItems(items)
  ) {
    return items
  }
  if (
    ALL_BC_PLAN_CODES.includes(productCode) &&
    hasKeapInItems(items) &&
    !hasBusinessCenterInItems(items)
  ) {
    return items
  }

  // --- Guard: Marketing Center and Signature (MC 2.0) can't coexist ---
  // Legacy Marketing Center (Plus/Pro/Pro Enhanced) is greyed out in the UI
  // while a Signature/TGP plan is in the cart (getDisabledPlanCodes); refuse
  // it here too so no caller can put both on the same account. Foundation is
  // exempt — it's the supported downgrade target for TGP accounts.
  if (
    LEGACY_MC5_CODES.includes(productCode) &&
    items.some(item => MC_2_0_PLANS.includes(item.productCode))
  ) {
    return items
  }

  // --- Rule 1: Group selection (replace / coexist / custom) ---
  const ruleName = GROUP_SELECTION_RULES[groupId] ?? 'replace'
  let result = RULES_BY_NAME[ruleName](items, newItem, groupPlanCodes)

  // --- Rule 1a: Leads Lift exemption from Growth Package group-replace ---
  // Leads Lift shares a catalog group with Growth Packages, so Rule 1's
  // replace drops it when a GP is added. GPs and Leads Lift can coexist in
  // AU/NZ (Leads Lift is only available there) — restore any Leads Lift item
  // that Rule 1 just removed.
  if (
    GROWTH_PACKAGES.includes(productCode) &&
    ['AU', 'NZ'].includes(selectedCountry)
  ) {
    const leadsLiftItem = items.find(
      item => item.productCode === LEADS_LIFT_M2M_CODE,
    )
    if (
      leadsLiftItem &&
      !result.some(item => item.productCode === LEADS_LIFT_M2M_CODE)
    ) {
      result = [...result, leadsLiftItem]
    }
  }

  // --- Rule 1a: one BC per order (FULSAS-15052) ---
  // Enterprise / 6.0 BC codes may be missing from the catalog group's visible
  // plans, so replaceWithinGroup above can leave an owned one next to the
  // newly added BC. Drop every other 5.x / Enterprise BC code so only one
  // remains. bc-6 is deliberately left alone: it is allowed to sit next to
  // another BC (see Rule 3b's noOtherBC check).
  if (ruleName === 'replace' && OTHER_BC_CODES.includes(productCode)) {
    result = result.filter(
      item =>
        item.productCode === productCode ||
        !OTHER_BC_CODES.includes(item.productCode),
    )
  }

  // --- Rule 1b: MC 2.0 / Thryv Growth Platform displacement ---
  // The account's existing MC 2.0 tier (e.g. signature) may be absent from
  // the catalog group's visible plans after filterGroupsByAccountContext runs,
  // so replaceWithinGroup above misses it in groupPlanCodes. When any MC 2.0
  // plan is added, explicitly remove every other MC 2.0 plan from the cart.
  // MC Foundation is in a different catalog group ('Marketing Center') so the
  // same-group replace above also misses it — handle it here too, mirroring
  // options.js:2200-2207's explicit Foundation displacement.
  if (
    MC_2_0_PLANS.includes(productCode) ||
    isTgpFoundationFamilyCode(productCode)
  ) {
    result = result.filter(
      item =>
        item.productCode === productCode ||
        !MC_2_0_PLANS.includes(item.productCode),
    )
  }

  // --- Rule 1b-2: GEP-7133 / FULSAS-13599 - TGP displaces legacy MC 5.0 ---
  // Adding a TGP (MC 2.0) plan is an in-place upgrade off MC 5.0 — the
  // account's legacy MC 5.0 plan (Foundation/Plus/Pro/Pro Enh) no longer
  // applies once TGP is selected, so remove it from the cart/order summary,
  // whether it's a fresh cart addition or an account-owned (alreadyIncluded)
  // item. This is unconditional removal (like Rule 1b/2c above), not a
  // downgrade-permission check — TGP replacing MC 5.0 is the whole point of
  // this upgrade flow, not a restricted downgrade.
  // TO REVERT: delete this block. The matching restore-on-remove branch
  // lives in resolveItemsAfterRemove (search "GEP-7133" there) and can be
  // reverted independently.
  const MC5_LEGACY_CODES_FOR_TGP_DISPLACEMENT = [
    ...MC_PLANS_CODE,
    'mc_pro_enh-5-thryv-m2m',
    MC_FOUNDATION_CODE,
    ...THRYV_BASIC_CODES,
  ]
  if (MC_2_0_PLANS.includes(productCode)) {
    result = result.filter(
      item => !MC5_LEGACY_CODES_FOR_TGP_DISPLACEMENT.includes(item.productCode),
    )
  }

  // --- Rule 1e: bundled website entitlement replaces the standalone add-on ---
  // A plan that carries ent-5-website (Signature/Premier bundle it) makes the
  // standalone Professionally Designed Website subscription redundant, so
  // drop it — including an account-owned one, which cancels it on submit.
  // Mirrors options.js:2213's `includesWebsite && websiteApp` removal.
  if (
    productCode !== WEBSITE_ADDON_CODE &&
    newItem.addons?.some(
      addon => (addon.productCode ?? addon.code) === WEBSITE_ENTITLEMENT_CODE,
    )
  ) {
    // On a real account the standalone website is an add-on nested inside the
    // account's marketplace wrapper (appmarket-5-thryv-m2m), not a top-level
    // item, so strip it from nested add-ons as well. The wrapper's unitPrice
    // is the whole subscription subtotal (website included), so subtract the
    // removed add-on's price too or Due Monthly keeps charging for it.
    result = result
      .filter(item => item.productCode !== WEBSITE_ADDON_CODE)
      .map(item => {
        const website = item.addons?.find(
          addon => (addon.productCode ?? addon.code) === WEBSITE_ADDON_CODE,
        )
        if (!website) return item
        return {
          ...item,
          ...(item.unitPrice != null && {
            unitPrice: Math.max(
              0,
              Number(item.unitPrice) - Number(website.unitPrice ?? 0),
            ),
          }),
          addons: item.addons.filter(addon => addon !== website),
        }
      })
  }

  // --- Rule 1c: Keap legacy product displacement ---
  // Legacy Keap products are not in groupPlanCodes (they're not shown in the
  // catalog), so replaceWithinGroup above leaves them in the cart alongside
  // the newly selected new product. Remove the legacy counterpart explicitly.
  if (KEAP_FAMILY_M2M_CODES.includes(productCode)) {
    result = result.filter(
      item =>
        item.productCode === productCode ||
        !KEAP_FAMILY_LEGACY_CODES.includes(item.productCode),
    )
  } else if (KEAP_FAMILY_ANNUAL_CODES.includes(productCode)) {
    result = result.filter(
      item =>
        item.productCode === productCode ||
        !KEAP_FAMILY_LEGACY_ANNUAL_CODES.includes(item.productCode),
    )
  }

  // --- Rule 1d: Keap Add-Ons parent wrapper injection ---
  // When a Keap center is added, ensure the correct parent wrapper is in the
  // cart and all others (including legacy) are removed.
  if (KEAP_FAMILY_CODES.includes(productCode)) {
    const isAnnual = KEAP_FAMILY_ANNUAL_CODES.includes(productCode)
    const neededParentForAddons = isAnnual
      ? 'addon-5-keap-addons-annual'
      : 'addon-5-keap-addons'
    const allOtherWrappers = [
      'addon-5-keap-addons',
      'addon-5-keap-addons-annual',
      'addon-5-keap-addons-legacy-m2m',
      'addon-5-keap-addons-legacy-annual',
    ].filter(code => code !== neededParentForAddons)
    // Also treat any already-included legacy wrapper as a valid parent — the
    // account's existing addons live inside it and it should not be replaced.
    const hasValidParentWrapper = result.some(
      item =>
        item.productCode === neededParentForAddons ||
        (item.alreadyIncluded && allOtherWrappers.includes(item.productCode)),
    )
    if (!hasValidParentWrapper) {
      result = result.filter(
        item =>
          !allOtherWrappers.includes(item.productCode) || item.alreadyIncluded,
      )
      result = [
        ...result,
        toApiItem({
          productId: neededParentForAddons,
          name: isAnnual ? 'Keap Add-Ons Yearly' : 'Keap Add-Ons',
          price: 0,
          quantity: 1,
          productType: 'APP',
          billingFrequency: isAnnual ? 'annual' : 'monthly',
          configuredOptions: {},
        }),
      ]
    }
  }

  // --- Rule 2: Excludes ---
  // Remove any cart items that the new product explicitly excludes. Always
  // skips MC codes when adding a Thryv Leads product — legacy catalog never
  // removes MC when TL is added, in either TL_MC_Access state (its excludes
  // field is only ever read for the disable-check direction, mirrored in
  // getDisabledPlanCodes; centers-and-apps.js:672-695's TL disable branches
  // only exist for isNewTLMCAccessible === true, so with the flag off TL is
  // never blocked by MC being in cart either — an asymmetric quirk
  // intentionally preserved here for parity, not something to "fix").
  const excludes = newItem.excludes ?? []
  if (excludes.length > 0) {
    const coexistExemptCodes = TL_CODES.includes(productCode)
      ? MC_CENTER_CODES
      : []
    result = result.filter(
      item =>
        item.productCode === productCode ||
        !excludes.includes(item.productCode) ||
        coexistExemptCodes.includes(item.productCode),
    )
  }

  // --- Rule 2a: disableApps / disableCenters - adding a plan actively removes any
  // already-selected items (new or account-owned) that it disables, rather
  // than preemptively greying them out before they're added (that aggregate
  // is dead code in the legacy catalog — see getDisabledPlanCodes' note).
  const newItemConfig = getProductConfig?.(productCode) ?? null
  const disabledByNewItem = [
    ...(newItemConfig?.disableApps ?? []),
    ...(newItemConfig?.disableCenters ?? []),
  ]
  if (disabledByNewItem.length > 0) {
    result = result.filter(
      item =>
        item.productCode === productCode ||
        !disabledByNewItem.includes(item.productCode),
    )
  }

  // --- Rule 2b: Growth Package mutual exclusion ---
  // Starter/Expanded Growth Package are mutually exclusive tiers — adding
  // either one removes the other, whether the removed one was added this
  // session or is an account-owned (alreadyIncluded) upgrade item. This
  // overrides their group's default 'coexist' rule (Rule 1), which would
  // otherwise leave both in the cart side by side.
  if (GROWTH_PACKAGES.includes(productCode)) {
    result = result.filter(
      item =>
        item.productCode === productCode ||
        !GROWTH_PACKAGES.includes(item.productCode),
    )
  }

  // --- Rule 2c: TL removes Growth Package (FULSAS-14189) ---
  // TL and GP are mutually exclusive for US clients — TL wins. Remove any GP
  // from the cart when TL is being added, whether GP is a top-level item or
  // nested inside an addon array (forward-compatible if GP ever lives under
  // appmarket). The disable side (greying out GP while TL is in cart) is
  // handled by getDisabledPlanCodes.
  if (TL_CODES.includes(productCode)) {
    result = result
      .filter(item => !GROWTH_PACKAGES.includes(item.productCode))
      .map(item =>
        item.addons
          ? {
              ...item,
              addons: item.addons.filter(
                a => !GROWTH_PACKAGES.includes(a.productCode),
              ),
            }
          : item,
      )
  }

  // --- Rule 2d: MC Foundation removes MC 2.0 plans ---
  // MC Foundation and MC 2.0 plans (mc_starter, mc_prov2, mc_premier, annual
  // variants) are mutually exclusive but live in different UI groups, so the
  // same-group replace rule above never removes them.
  if (productCode === MC_FOUNDATION_CODE) {
    result = result.filter(
      item =>
        item.productCode === productCode ||
        !MC_2_0_PLANS.includes(item.productCode),
    )
  }

  // --- Rule 2c-2: FULSAS-13789 - TGP Foundation / Thryv Basic drops Growth
  // Packages. Growth Packages are not allowed alongside either plan, so a
  // cart-only Growth Package is removed when one of them is added.
  if (isTgpFoundationFamilyCode(productCode)) {
    result = result.filter(
      item =>
        !GROWTH_PACKAGES.includes(item.productCode) || item.alreadyIncluded,
    )
  }

  // --- Rule 2d: FULSAS-13789 - legacy MC / BC displaces Thryv Basic ---
  // Thryv Basic is not applicable alongside a legacy Marketing Center or
  // Business Center plan, so adding one drops a cart-only Thryv Basic.
  // (TGP Foundation is exempt: Rules 3e/3g auto-add it alongside BC.)
  if (isTgpFoundationBlockingCode(productCode)) {
    result = result.filter(
      item => !isThryvBasicCode(item.productCode) || item.alreadyIncluded,
    )
  }

  // --- Rule 3: Auto-add centers ---
  // Add any centers that the new product requires but aren't already in the cart.
  const autoAddCenters = newItem.autoAddCenters ?? []
  if (autoAddCenters.length > 0) {
    const existingCodes = new Set(result.map(item => item.productCode))
    const centersToAdd = autoAddCenters
      .filter(center => !existingCodes.has(center.code))
      .map(center =>
        toApiItem({
          productId: center.code,
          name: center.name,
          price: getPriceForCountry(center.currencies, selectedCountry),
          productType: 'CENTER',
          quantity: 1,
          billingFrequency:
            center.productBillingType === 'Annual' ? 'annual' : 'monthly',
          configuredOptions: {},
        }),
      )
    result = [...result, ...centersToAdd]
  }

  // --- Rule 3b: Auto-add one-time fees ---
  // e.g. the Website Design Fee alongside Professionally Designed Website —
  // callers attach autoAddOneTimeFees to newItem (see WebsiteAddonPanel and
  // ProductPlanSelector's ent-5-website handling), skipping it entirely when
  // the fee should be waived rather than filtering it out after the fact.
  const autoAddOneTimeFees = newItem.autoAddOneTimeFees ?? []
  if (autoAddOneTimeFees.length > 0) {
    const existingFeeCodes = new Set(result.map(item => item.productCode))
    const feesToAdd = autoAddOneTimeFees
      .filter(fee => !existingFeeCodes.has(fee.code))
      .map(fee =>
        toApiItem({
          productId: fee.code,
          name: fee.name,
          price: getPriceForCountry(fee.currencies, selectedCountry),
          productType: 'ONE_TIME_FEE',
          billingFrequency: null,
          quantity: 1,
          configuredOptions: {},
        }),
      )
    result = [...result, ...feesToAdd]
  }

  // --- Rule 3c: Auto-add items (apps or one-time fees) ---
  // Used by panels that need to inject a companion product alongside the main
  // one (e.g. HVAC auto-injecting the appropriate vertical clientsvcs product).
  // Items in autoAddItems are skipped if the same code is already in the cart.
  const autoAddItems = newItem.autoAddItems ?? []
  if (autoAddItems.length > 0) {
    const existingItemCodes = new Set(result.map(item => item.productCode))
    const itemsToAdd = autoAddItems
      .filter(item => !existingItemCodes.has(item.code))
      .map(item => {
        const config =
          getAutoAddItemConfig?.(item.code) ??
          getProductConfig?.(item.code) ??
          null
        const price = config
          ? getPriceForCountry(config.currencies, selectedCountry)
          : item.price ?? 0
        return toApiItem({
          productId: item.code,
          name: item.name ?? '',
          price,
          productType: item.productType ?? 'APP',
          billingFrequency:
            (item.productType ?? 'APP') === 'ONE_TIME_FEE'
              ? null
              : item.billingFrequency ?? 'monthly',
          quantity: 1,
          configuredOptions: {},
        })
      })
    result = [...result, ...itemsToAdd]
  }

  // --- Rule 3d: HVAC vertical client services resolution ---
  // Triggers whenever HVAC, MC Plus/Pro, or a website product is added and
  // HVAC is present in the resulting cart.  Rule 3c (autoAddItems) may have
  // already injected a preliminary vertical code from the panel — this rule
  // overrides it with the authoritative code derived from full cart state,
  // applying the same logic as legacy options.js:2592-2731:
  //   • isMCPlusProInDefault  →  base variant regardless of cart MC tier
  //   • MC Pro + ent-5-website  →  vert_mc_web_clientsvcs
  //   • MC Pro (no website)     →  vert_mc_clientsvcs
  //   • MC Plus                 →  vert_mc_clientsvcs
  //   • no MC                   →  vert_clientsvcs
  // Fee waiver: thryvprfee and thryvdesg2 are stripped via the excludes field
  // that HvacAddonPanel attaches to the HVAC product (Rule 2 above).
  const hasHvacInResult = result.some(item =>
    HVAC_CODES.includes(item.productCode),
  )
  const isHvacMcOrWebAdded =
    HVAC_CODES.includes(productCode) ||
    productCode === 'mc_plus-5-thryv-m2m' ||
    productCode === 'mc_pro-5-thryv-m2m' ||
    productCode === WEBSITE_ADDON_CODE

  if (hasHvacInResult && isHvacMcOrWebAdded) {
    result = swapHvacVertical(
      result,
      getAutoAddItemConfig ?? getProductConfig,
      selectedCountry,
    )
  }

  // --- Rule 3e: Auto-add MC Foundation (US only) ---
  const ALL_BC_CODES = [...OTHER_BC_CODES, BC_6_CODE]
  const MC_PLUS_PRO_ENH_CODES = [...MC_PLANS_CODE, 'mc_pro_enh-5-thryv-m2m']
  const hasBCInAccount = result.some(
    item => ALL_BC_CODES.includes(item.productCode) && item.alreadyIncluded,
  )
  const hasBCInResult = result.some(item =>
    ALL_BC_CODES.includes(item.productCode),
  )
  const hasMCInResult = result.some(item =>
    MC_PLUS_PRO_ENH_CODES.includes(item.productCode),
  )
  const hasMC20InResult = result.some(item =>
    MC_2_0_PLANS.includes(item.productCode),
  )
  const hasMCFoundationInResult = result.some(item =>
    isTgpFoundationFamilyCode(item.productCode),
  )

  // When SEO Base is added or TL is already in the cart, and the account owns
  // a BC plan but has no MC plan → auto-add MC Foundation.
  if (
    (productCode === 'seobase-5-thryv-m2m' ||
      items.some(item => TL_CODES.includes(item.productCode))) &&
    !hasMCInResult &&
    !hasMC20InResult &&
    hasBCInAccount &&
    !hasMCFoundationInResult &&
    selectedCountry === 'US'
  ) {
    const foundationConfig =
      getAutoAddItemConfig?.(MC_FOUNDATION_CODE) ??
      getProductConfig?.(MC_FOUNDATION_CODE)
    if (foundationConfig) {
      result = [
        ...result,
        toApiItem({
          productId: MC_FOUNDATION_CODE,
          name: foundationConfig.name,
          price: getPriceForCountry(
            foundationConfig.currencies,
            selectedCountry,
          ),
          productType: 'CENTER',
          quantity: 1,
          billingFrequency: 'monthly',
          configuredOptions: {},
        }),
      ]
    }
  }

  // When a Social Ads product is added and BC is in the cart but no MC → auto-add MC Foundation.
  if (
    productCode.includes('addon-socialads') &&
    !hasMCInResult &&
    !hasMCFoundationInResult &&
    !hasMC20InResult &&
    hasBCInResult &&
    selectedCountry === 'US'
  ) {
    const foundationConfig =
      getAutoAddItemConfig?.(MC_FOUNDATION_CODE) ??
      getProductConfig?.(MC_FOUNDATION_CODE)
    if (foundationConfig) {
      result = [
        ...result,
        toApiItem({
          productId: MC_FOUNDATION_CODE,
          name: foundationConfig.name,
          price: getPriceForCountry(
            foundationConfig.currencies,
            selectedCountry,
          ),
          productType: 'CENTER',
          quantity: 1,
          billingFrequency: 'monthly',
          configuredOptions: {},
        }),
      ]
    }
  }

  // --- Rule 4: Downgrade cascade ---
  // When the new item displaces a higher-ranked item in the same group,
  // strip any dependent apps/entitlements listed in newItem.downgrades.
  const downgrades = newItem.downgrades ?? []
  const isDowngrade =
    downgrades.length > 0 &&
    items.some(
      item =>
        groupPlanCodes.includes(item.productCode) &&
        item.productCode !== productCode &&
        (item.productRank ?? 0) > (newItem.productRank ?? 0),
    )

  if (isDowngrade) {
    const removeCodes = new Set(downgrades.map(d => d.dependentProductCode))
    result = result.filter(item => !removeCodes.has(item.productCode))
  }

  // --- Rule 5: requires/requiresCenter/requiresAddon cascade ---
  // If any rule above removed a product that was enabling another item via
  // requires/requiresCenter/requiresAddon, remove those now-orphaned
  // newly-added items too.
  if (getProductConfig) {
    result = cascadeRequiresRemoval(result, getProductConfig)
  }

  // Normalize all items to API schema fields (+ alreadyIncluded) before storing in Redux
  return result.map(toApiItem)
}

// Replaces an already-added item in place by productCode, preserving its
// position — used when editing a plan that's already in the order (e.g.
// bumping AEO & SEO PowerBoost/Keywords quantities via SeoAddonPanel) so it
// doesn't get gated by resolveItemsAfterAdd's existingCount/maxQty check.
export function resolveItemsAfterUpdate(items, updatedItem) {
  const apiItem = toApiItem(updatedItem)

  const exists = items.some(item => item.productCode === apiItem.productCode)
  if (!exists) return [...items, apiItem]
  return items.map(item =>
    item.productCode === apiItem.productCode ? apiItem : item,
  )
}
